import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { trackEvent, trackPageView } from '../services/analytics';
import { Pause, Play, ChevronLeft, ChevronRight, Home, Lock } from 'lucide-react';
import { getAudioLocal, hasAudioLocal } from '../utils/localAudioCache';
import { verifyAndGetCachedStory } from '../utils/verifyUserData';
import { getFromGlobalStore } from '../utils/globalAudioStore';
import { rlog } from '../utils/railwayLogger';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

// Planet icons and colors - Clean astrological symbols only
const PLANET_ICONS = {
  Sun: '☉', Moon: '☽', Mars: '♂', Mercury: '☿', Jupiter: '♃',
  Venus: '♀', Saturn: '♄', Rahu: '☊', Ketu: '☋'
};

const PLANET_COLORS = {
  Sun: '#FFD700', Moon: '#C8C8FF', Mars: '#DC143C', Mercury: '#32CD32',
  Jupiter: '#FFA500', Venus: '#FF69B4', Saturn: '#8B4513', Rahu: '#4B0082', Ketu: '#006400'
};

// All 8 domains in order - Clean names only, no emojis
const ALL_DOMAINS = [
  { key: 'health', name: 'Health', fullName: 'Health & Longevity' },
  { key: 'career', name: 'Career', fullName: 'Career & Success' },
  { key: 'marriage', name: 'Love', fullName: 'Love & Marriage' },
  { key: 'wealth', name: 'Wealth', fullName: 'Wealth & Finance' },
  { key: 'family', name: 'Family', fullName: 'Family & Children' },
  { key: 'education', name: 'Education', fullName: 'Education & Skills' },
  { key: 'spiritual', name: 'Spiritual', fullName: 'Spirituality & Dharma' },
  { key: 'travel', name: 'Travel', fullName: 'Travel & Foreign' }
];

// Language configuration
const LANGUAGES = ['hindi', 'hinglish', 'english'];
const LANGUAGE_LABELS = { hindi: 'हिंदी', hinglish: 'Hinglish', english: 'English' };

// === 12-MONTH DRIP SYSTEM ===
// 30-day unlock cycles from subscription start
const getSubscriptionStartTs = () => {
  try {
    const subData = localStorage.getItem('subscriptionData');
    if (!subData) return null;
    const sub = JSON.parse(subData);
    const startStr = sub.started_at || sub.created_at || sub.subscribed_at;
    if (!startStr) return null;
    const t = new Date(startStr).getTime();
    return isNaN(t) ? null : t;
  } catch { return null; }
};

// Calculate how many 30-day parts are unlocked since subscription start (1..12)
const getUnlockedMonths = () => {
  const start = getSubscriptionStartTs();
  if (!start) return 1; // Default: first part unlocked
  const daysSince = Math.floor((Date.now() - start) / 86400000);
  const parts = Math.floor(daysSince / 30) + 1; // part 1 on day 0
  return Math.max(1, Math.min(12, parts));
};

// Days until next 30-day part unlocks
const getDaysUntilNextUnlock = () => {
  const start = getSubscriptionStartTs();
  if (!start) return 30;
  const daysSince = Math.floor((Date.now() - start) / 86400000);
  const daysIntoCycle = daysSince % 30;
  return Math.max(1, 30 - daysIntoCycle);
};

// TTS Configuration
const TTS_ENDPOINT = '/api/tts/edge';
const MAX_CHUNK_CHARS = 400;

// Split text into chunks
const splitTextIntoChunks = (text, maxChars = MAX_CHUNK_CHARS) => {
  if (!text || text.length <= maxChars) return text ? [text] : [];
  
  const chunks = [];
  let remaining = text;
  
  while (remaining.length > 0) {
    if (remaining.length <= maxChars) {
      chunks.push(remaining.trim());
      break;
    }
    
    let breakPoint = maxChars;
    for (const endChar of ['. ', '। ', '? ', '! ', '\n\n', '\n', ', ']) {
      const lastIndex = remaining.substring(0, maxChars).lastIndexOf(endChar);
      if (lastIndex > maxChars * 0.5) {
        breakPoint = lastIndex + endChar.length;
        break;
      }
    }
    
    chunks.push(remaining.substring(0, breakPoint).trim());
    remaining = remaining.substring(breakPoint).trim();
  }
  
  return chunks;
};

// Clean narration text - remove separators, markdown, and headers that shouldn't be spoken
const cleanNarrationText = (text) => {
  if (!text) return '';
  return text
    .replace(/---\s*[A-Z_]+\s*---/gi, '')    // Remove ---MARKER--- patterns
    .replace(/===\s*[A-Z_]+\s*===/gi, '')    // Remove ===MARKER=== patterns
    .replace(/\b[A-Z_]{4,}---/g, '')          // Remove FUTURE_MD--- partial markers
    .replace(/\bFUTURE_MD\b/g, '')
    .replace(/\bPAST_MD\b/g, '')
    .replace(/\bFUTURE_NARRATIVES\b/g, '')
    .replace(/\bPAST_NARRATIVES\b/g, '')
    .replace(/###SECT_[A-Z_]+###/g, '')
    .replace(/###\s*/g, '')
    .replace(/---+/g, '')                      // Remove --- separators
    .replace(/\*\*([^*]+)\*\*/g, '$1')         // Remove **bold** markers but keep text
    .replace(/#{1,3}\s+/g, '')                 // Remove ## headers
    .replace(/^\s*OVERVIEW\s*$/gim, '')         // Remove OVERVIEW headers
    .replace(/^\s*Domain\s*\d+\s*$/gim, '')     // Remove "Domain 1" headers
    // Remove duplicate mahadasha headers: "Venus Mahadasha (2007-2027)\n" at start of text
    .replace(/^\s*\w+\s+Mahadasha\s*\(\d{4}\s*[-–to]+\s*\d{4}\)\s*\n?/i, '')
    // Also remove mahadasha headers that appear after newlines (e.g., mid-text duplicates)
    .replace(/\n\s*\w+\s+Mahadasha\s*\(\d{4}\s*[-–to]+\s*\d{4}\)\s*\n/gi, '\n')
    .replace(/\n{3,}/g, '\n\n')                // Collapse multiple newlines
    .trim();
};

const FutureStoryExact = () => {
  const navigate = useNavigate();
  const { domainKey, domainId } = useParams();
  
  // Map numeric domainId to domain key
  const numericToKeyMap = {
    '1': 'health',
    '2': 'career', 
    '3': 'marriage',
    '4': 'wealth',
    '5': 'family',
    '6': 'education',
    '7': 'spiritual',
    '8': 'travel'
  };
  
  // Valid domain keys for direct string matching
  const validDomainKeys = ['health', 'career', 'marriage', 'wealth', 'family', 'education', 'spiritual', 'travel'];
  
  // Get the actual domain key (supports both /domain-story/1, /domain-story/health, and /future-story/health)
  // Priority: domainKey param > domainId if it's a valid key > numeric mapping > default
  const resolvedDomainKey = domainKey || 
    (validDomainKeys.includes(domainId) ? domainId : null) || 
    numericToKeyMap[domainId] || 
    'health';
  
  // Find current domain index
  const currentDomainIndex = ALL_DOMAINS.findIndex(d => d.key === resolvedDomainKey) >= 0 
    ? ALL_DOMAINS.findIndex(d => d.key === resolvedDomainKey) 
    : 0;
  const currentDomain = ALL_DOMAINS[currentDomainIndex] || ALL_DOMAINS[0];

  useEffect(() => {
    trackPageView('future_reels');
    trackEvent('future_reels_viewed', { domain: resolvedDomainKey });
  }, [resolvedDomainKey]);
  
  // Core states - check cache for instant load
  const [isLoading, setIsLoading] = useState(() => {
    // Don't show loading if we have cached future story data with actual reels content
    try {
      const birthRaw = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
      const currentName = birthRaw ? (JSON.parse(birthRaw).name || '').toLowerCase().trim() : '';
      
      const unified = verifyAndGetCachedStory();
      if (unified) {
        const ud = unified;
        const cachedName = (ud.user_info?.name || '').toLowerCase().trim();
        // Name mismatch — treat as no data
        if (currentName && cachedName && currentName !== cachedName) {
          return true;
        }
        // Check if actual future reel CONTENT exists (not just empty structure)
        const reels = ud.future_story?.domain_reels || {};
        const hasReelContent = Object.values(reels).some(langReels => {
          const actual = (typeof langReels === 'object') ? langReels : {};
          return Object.values(actual).some(v => typeof v === 'string' && v.length > 50);
        });
        if (hasReelContent) return false; // Real content exists
        // Even without reels, if future_story has mahadashas we can show the page
        if (ud.future_story?.mahadashas?.length > 0 || ud.future_mahadashas?.length > 0) {
          return false;
        }
      }
      const futureStory = localStorage.getItem('unified_future_story');
      if (futureStory) return false;
    } catch (e) { /* ignore */ }
    return true;
  });
  const [selectedLanguage, setSelectedLanguage] = useState('hinglish');
  const [personName, setPersonName] = useState('');
  const [birthYear, setBirthYear] = useState(1997);
  const [futureMahadashas, setFutureMahadashas] = useState([]);
  
  // 12-month drip system
  const [unlockedMonths, setUnlockedMonths] = useState(getUnlockedMonths);
  const [daysUntilUnlock, setDaysUntilUnlock] = useState(getDaysUntilNextUnlock);
  
  // Audio states - per language tracking
  const [audioChunks, setAudioChunks] = useState({ hindi: [], hinglish: [], english: [] });
  const [playbackPosition, setPlaybackPosition] = useState({ hindi: 0, hinglish: 0, english: 0 });
  const [audioTimePosition, setAudioTimePosition] = useState({ hindi: 0, hinglish: 0, english: 0 });
  
  // Playback states
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isPreloading, setIsPreloading] = useState(false);
  const [preloadProgress, setPreloadProgress] = useState(0);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [currentMahadashaIndex, setCurrentMahadashaIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [storyComplete, setStoryComplete] = useState(false);
  
  // Refs
  const audioRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const abortRef = useRef(false);
  const textIntervalRef = useRef(null);
  const storyDataRef = useRef(null);
  const fullStoryTextRef = useRef({ hindi: '', hinglish: '', english: '' });
  const audioUrlCacheRef = useRef({ hindi: {}, hinglish: {}, english: {} });
  const currentLanguageRef = useRef('hinglish');
  const previousTextRef = useRef('');
  const isUpdatingTextRef = useRef(false);
  // Refs to track positions reliably (avoid stale closure issues)
  const playbackPositionRef = useRef({ hindi: 0, hinglish: 0, english: 0 });
  // 12-month drip: how many chunks are unlocked per language (stop audio past this)
  const unlockedChunkCountRef = useRef({ hindi: 0, hinglish: 0, english: 0 });
  // 12-month drip: which mahadasha indices are unlocked (at least 1 chunk in the 1/12 slice)
  const [unlockedMdIndices, setUnlockedMdIndices] = useState([]);
  // 12-month drip: the "primary" active mahadasha for this monthly slice (FIRST mahadasha in slice)
  const [activeDripMdIdx, setActiveDripMdIdx] = useState(0);
  const audioTimePositionRef = useRef({ hindi: 0, hinglish: 0, english: 0 });
  // Refs to track current playback state (for domain position persistence)
  const currentChunkIndexRef = useRef(0);
  const currentMahadashaIndexRef = useRef(-1);
  const displayedTextRef = useRef('');
  // Per-domain position memory - persists across domain switches AND dashboard visits
  const domainPositionsRef = useRef(() => {
    try {
      const saved = sessionStorage.getItem('future_reel_positions');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  // Initialize ref value from the factory function
  if (typeof domainPositionsRef.current === 'function') {
    domainPositionsRef.current = domainPositionsRef.current();
  }
  // Autoplay pending ref - signals that data just loaded and should autoplay
  const autoplayPendingRef = useRef(false);
  // Pending play ref - user pressed play before chunks were ready
  const pendingPlayRef = useRef(false);
  // Track if first audio chunk is pre-fetched and ready for instant play
  const [firstChunkReady, setFirstChunkReady] = useState(false);
  // Track if full cached audio is available from IndexedDB (pre-loaded during payment)
  const fullAudioCacheRef = useRef({}); // { [language]: base64 }
  const fullAudioBlobUrlRef = useRef({}); // { [language]: blobUrl } — pre-created for instant switching
  
  // Save domain positions to sessionStorage whenever they change
  const saveDomainPositions = useCallback(() => {
    try {
      // Save current domain position before persisting
      if (resolvedDomainKey) {
        domainPositionsRef.current[resolvedDomainKey] = {
          playback: { ...playbackPositionRef.current },
          audioTime: { ...audioTimePositionRef.current },
          chunkIndex: currentChunkIndexRef.current,
          mahadashaIndex: currentMahadashaIndexRef.current,
          displayedText: displayedTextRef.current,
          language: selectedLanguage
        };
      }
      sessionStorage.setItem('future_reel_positions', JSON.stringify(domainPositionsRef.current));
    } catch(e) { /* ignore quota errors */ }
  }, [resolvedDomainKey, selectedLanguage]);
  
  // Keep refs in sync with state for reliable domain position saving
  useEffect(() => { currentChunkIndexRef.current = currentChunkIndex; }, [currentChunkIndex]);
  useEffect(() => { currentMahadashaIndexRef.current = currentMahadashaIndex; }, [currentMahadashaIndex]);
  useEffect(() => { displayedTextRef.current = displayedText; }, [displayedText]);
  
  // Scroll text box to top when text changes
  useEffect(() => {
    const el = document.getElementById('future-text-box');
    if (el) el.scrollTop = 0;
  }, [displayedText]);
  
  // Container ref
  const containerRef = useRef(null);
  
  // Track which domains have audio ready (for domain pills)
  const audioReadyDomainsRef = useRef({});
  useEffect(() => {
    const checkDomainReadiness = async () => {
      const userId = localStorage.getItem('user_id') || localStorage.getItem('taraanaya_device_id') || 'default';
      const domains = ['health', 'career', 'wealth', 'marriage', 'family', 'education', 'travel', 'spiritual'];
      const ready = {};
      for (const dk of domains) {
        try {
          const has = await hasAudioLocal(userId, `domain_${dk}`, 'hinglish');
          ready[dk] = !!has;
        } catch { ready[dk] = false; }
      }
      audioReadyDomainsRef.current = ready;
    };
    checkDomainReadiness();
    // Re-check every 5 seconds while audio is being generated
    const interval = setInterval(checkDomainReadiness, 5000);
    return () => clearInterval(interval);
  }, []);

  // Smooth text update function
  const updateDisplayedText = useCallback((newText) => {
    if (newText === previousTextRef.current || isUpdatingTextRef.current) return;
    
    isUpdatingTextRef.current = true;
    previousTextRef.current = newText;
    setDisplayedText(newText);
    
    setTimeout(() => {
      isUpdatingTextRef.current = false;
    }, 100);
  }, []);

  // Stop all audio - saves position before stopping
  const stopAllAudio = useCallback((savePosition = false) => {
    // Save current position before stopping (if requested)
    if (savePosition && audioRef.current) {
      const currentTime = audioRef.current.currentTime || 0;
      setAudioTimePosition(prev => ({ ...prev, [currentLanguageRef.current]: currentTime }));
    }
    
    // Pause the persistent audio element (don't destroy it)
    const audio = document.getElementById('future-story-audio');
    if (audio) {
      audio.pause();
      audio.onended = null;
      audio.onerror = null;
    }
    
    if (textIntervalRef.current) {
      clearInterval(textIntervalRef.current);
      textIntervalRef.current = null;
    }
    
    abortRef.current = true;
  }, []);

  // Convert base64 to blob URL
  // Convert base64 → universally-playable audio source.
  // Earlier used URL.createObjectURL(blob) but Android WebView silently
  // refuses to play blob: URLs with HTML5 Audio. Data URLs work on all
  // platforms (web, iOS, Android) reliably.
  const base64ToAudioUrl = (base64) => `data:audio/mp3;base64,${base64}`;

  // Get audio for a chunk — checks global store (0ms) → IndexedDB → API
  const getAudioForChunk = async (text, language, chunkIndex) => {
    const textHash = text.slice(0, 50).replace(/\s+/g, '_');
    const cacheKey = `${chunkIndex}_${textHash}`;
    
    // LAYER 0: In-memory cache (instant)
    if (audioUrlCacheRef.current[language]?.[cacheKey]) {
      return audioUrlCacheRef.current[language][cacheKey];
    }
    
    // LAYER 1: Global audio store (synchronous, 0ms — populated by loading screens)
    const globalAudio = getFromGlobalStore(language, text);
    if (globalAudio) {
      if (!audioUrlCacheRef.current[language]) audioUrlCacheRef.current[language] = {};
      audioUrlCacheRef.current[language][cacheKey] = globalAudio.blobUrl || globalAudio.dataUrl;
      return globalAudio.blobUrl || globalAudio.dataUrl;
    }
    
    // LAYER 2: IndexedDB per-chunk cache (async, ~50ms)
    try {
      const userId = localStorage.getItem('user_id') || localStorage.getItem('session_id') || 'anon';
      const idbKey = `fs_${language}_${text.substring(0, 40).replace(/\s+/g, '_')}`;
      const cachedBase64 = await getAudioLocal(userId, 'future_story_chunk', idbKey);
      if (cachedBase64 && cachedBase64.length > 1000) {
        const audioUrl = base64ToAudioUrl(cachedBase64);
        if (!audioUrlCacheRef.current[language]) audioUrlCacheRef.current[language] = {};
        audioUrlCacheRef.current[language][cacheKey] = audioUrl;
        return audioUrl;
      }
    } catch (_) { /* IndexedDB unavailable */ }
    
    // LAYER 3: Backend API (slow, 2-5s)
    try {
      const response = await fetch(`${API_URL}${TTS_ENDPOINT}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.substring(0, 500), language })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.audio) {
          const audioUrl = base64ToAudioUrl(result.audio);
          if (!audioUrlCacheRef.current[language]) audioUrlCacheRef.current[language] = {};
          audioUrlCacheRef.current[language][cacheKey] = audioUrl;
          
          // Save to IndexedDB for persistence
          try {
            const { default: LocalAudioCache } = await import('../utils/localAudioCache');
            const userId = localStorage.getItem('user_id') || localStorage.getItem('session_id') || 'anon';
            const idbKey = `fs_${language}_${text.substring(0, 40).replace(/\s+/g, '_')}`;
            await LocalAudioCache.saveAudioLocal(userId, 'future_story_chunk', idbKey, result.audio);
          } catch (_) {}
          
          return audioUrl;
        }
      }
    } catch (e) {
      console.error('Audio fetch error:', e.message);
    }
    return null;
  };

  // Play a single audio chunk using persistent DOM audio element (same as PastStory)
  const playAudioChunk = (audioUrl, chunkText, startTime = 0) => {
    return new Promise((resolve) => {
      // Always show text first
      updateDisplayedText(chunkText);
      
      if (abortRef.current || !audioUrl) {
        const readTime = Math.max(chunkText.split(/\s+/).length * 250, 2000);
        setTimeout(resolve, readTime);
        return;
      }
      
      // Use the persistent DOM audio element
      let audio = document.getElementById('future-story-audio');
      if (!audio) {
        audio = new Audio();
        audio.setAttribute('playsinline', 'true');
      }
      
      audioRef.current = audio;
      
      let resolved = false;
      const safeResolve = () => {
        if (!resolved) {
          resolved = true;
          resolve();
        }
      };
      
      // Clear all previous listeners
      audio.onended = null;
      audio.onerror = null;
      audio.oncanplay = null;
      audio.onloadeddata = null;
      audio.pause();
      
      audio.onended = () => safeResolve();
      audio.onerror = () => safeResolve();
      
      // Handler to start playback once audio data is ready
      let playAttempted = false;
      const tryPlay = () => {
        if (playAttempted || resolved || abortRef.current) return;
        playAttempted = true;
        audio.oncanplay = null;
        audio.onloadeddata = null;
        if (abortRef.current) { safeResolve(); return; }
        if (startTime > 0) {
          audio.currentTime = startTime;
        }
        audio.play()
          .then(() => {})
          .catch(() => {
            const readTime = Math.max(chunkText.split(/\s+/).length * 250, 3000);
            setTimeout(safeResolve, readTime);
          });
      };
      
      // Set BOTH canplay and loadeddata handlers BEFORE setting src
      // This prevents missing the event if audio loads synchronously (e.g., blob URLs)
      audio.oncanplay = tryPlay;
      audio.onloadeddata = tryPlay;
      
      // Set new source AFTER handlers are attached
      audio.src = audioUrl;
      audio.volume = 1.0;
      
      // If readyState already indicates data is available (cached blob), play immediately
      if (audio.readyState >= 3) {
        tryPlay();
      }
      
      // Fallback: if neither event fires within 2s, try playing anyway
      setTimeout(() => {
        if (!playAttempted && !resolved && !abortRef.current) {
          tryPlay();
        }
      }, 2000);
      
      // Ultimate fallback (60 seconds max per chunk)
      setTimeout(() => safeResolve(), 60000);
    });
  };

  // Play full cached audio from IndexedDB with text chunk sync
  const playFullCachedAudio = (base64Audio, chunks, startFromIndex = 0) => {
    return new Promise((resolve) => {
      const audio = document.getElementById('future-story-audio');
      if (!audio || !base64Audio) { resolve(); return; }
      
      audioRef.current = audio;
      audio.pause();
      audio.onended = null;
      audio.onerror = null;
      audio.ontimeupdate = null;
      audio.oncanplay = null;
      audio.onloadedmetadata = null;
      
      // Use pre-created blob URL if available (instant), otherwise create new one
      const lang = currentLanguageRef.current;
      const audioUrl = fullAudioBlobUrlRef.current[lang] || base64ToAudioUrl(base64Audio);
      
      // Calculate proportional timing for each chunk based on text length
      const totalTextLen = chunks.reduce((sum, c) => sum + c.text.length, 0);
      
      let resolved = false;
      const safeResolve = () => { if (!resolved) { resolved = true; resolve(); } };
      
      const setupPlayback = () => {
        const totalDuration = audio.duration;
        if (!totalDuration || totalDuration <= 0) { safeResolve(); return; }
        
        // Build chunk timing map
        let cumTime = 0;
        const chunkTimings = chunks.map((c, idx) => {
          const start = cumTime;
          const duration = (c.text.length / totalTextLen) * totalDuration;
          cumTime += duration;
          return { start, end: cumTime, idx };
        });

        // 12-month drip boundary: compute the audio time at which 1/12 slice ends.
        const unlockedCount = Math.min(
          unlockedChunkCountRef.current[currentLanguageRef.current] || chunks.length,
          chunks.length
        );
        const unlockedEndTime = unlockedCount > 0
          ? chunkTimings[Math.max(0, unlockedCount - 1)].end
          : totalDuration;
        
        // Seek to start position
        const seekTime = chunkTimings[startFromIndex]?.start || 0;
        audio.currentTime = seekTime;
        
        // Show initial text
        let currentIdx = startFromIndex;
        updateDisplayedText(chunks[currentIdx].text);
        setCurrentChunkIndex(currentIdx);
        // Animation follows the CURRENT chunk's mahadasha (overview maps to first future MD)
        const initialMdForIdx = chunks[currentIdx].mahadashaIndex >= 0 ? chunks[currentIdx].mahadashaIndex : 0;
        setCurrentMahadashaIndex(initialMdForIdx);
        playbackPositionRef.current[currentLanguageRef.current] = currentIdx;
        
        // Track text advancement on timeupdate + stop at 1/12 boundary
        audio.ontimeupdate = () => {
          if (abortRef.current) return;
          const ct = audio.currentTime;
          
          // 1/12 DRIP LOCK: pause audio + mark complete when we cross the unlock boundary
          if (ct >= unlockedEndTime && unlockedCount < chunks.length) {
            audio.pause();
            audio.ontimeupdate = null;
            setStoryComplete(true);
            setIsPlaying(false);
            setShowSkipButton(false);
            playbackPositionRef.current[currentLanguageRef.current] = unlockedCount;
            setPlaybackPosition(prev => ({ ...prev, [currentLanguageRef.current]: unlockedCount }));
            safeResolve();
            return;
          }
          
          for (let i = currentIdx; i < Math.min(chunkTimings.length, unlockedCount); i++) {
            if (ct >= chunkTimings[i].start && (i === chunkTimings.length - 1 || ct < chunkTimings[i].end)) {
              if (i !== currentIdx) {
                currentIdx = i;
                updateDisplayedText(chunks[i].text);
                setCurrentChunkIndex(i);
                // Animation follows currently playing chunk's mahadasha.
                // Overview chunks (mahadashaIndex = -1) keep animation on first future MD (0/Rahu).
                const mdForChunk = chunks[i].mahadashaIndex >= 0 ? chunks[i].mahadashaIndex : 0;
                setCurrentMahadashaIndex(mdForChunk);
                playbackPositionRef.current[currentLanguageRef.current] = i;
                setPlaybackPosition(prev => ({ ...prev, [currentLanguageRef.current]: i }));
              }
              break;
            }
          }
        };
        
        audio.onended = () => {
          audio.ontimeupdate = null;
          setStoryComplete(true);
          setIsPlaying(false);
          setShowSkipButton(false);
          playbackPositionRef.current[currentLanguageRef.current] = chunks.length;
          setPlaybackPosition(prev => ({ ...prev, [currentLanguageRef.current]: chunks.length }));
          safeResolve();
        };
        
        audio.onerror = () => safeResolve();
        audio.volume = 1.0;
        audio.play().catch(() => safeResolve());
      };
      
      // Set handlers BEFORE src
      audio.onloadedmetadata = setupPlayback;
      audio.src = audioUrl;
      
      // Fallback if loadedmetadata already fired (cached blob)
      if (audio.readyState >= 1 && audio.duration > 0) {
        setupPlayback();
      }
      
      // Safety timeout
      setTimeout(() => safeResolve(), 300000); // 5 min max
    });
  };

  // Try to load full audio from IndexedDB (inline, called at play time)
  const tryLoadCachedAudio = async (lang) => {
    if (fullAudioCacheRef.current[lang]) return fullAudioCacheRef.current[lang];
    try {
      // Use tts_user_id first (set during TTS generation), then fall back to standard keys
      const userId = localStorage.getItem('tts_user_id') || localStorage.getItem('user_id') || localStorage.getItem('audio_user_id') || localStorage.getItem('session_id') || localStorage.getItem('taraanaya_device_id') || 'default';
      const cached = await getAudioLocal(userId, `domain_${resolvedDomainKey}`, lang);
      if (cached) {
        fullAudioCacheRef.current[lang] = cached;
        fullAudioBlobUrlRef.current[lang] = base64ToAudioUrl(cached);
        setFirstChunkReady(true);
        return cached;
      }
    } catch (e) { /* IndexedDB not available */ }
    return null;
  };

  // Pre-buffer next audio for seamless playback (like PastStory)
  const nextAudioRef = useRef(null);
  
  const preloadNextChunk = (loadedChunks, currentIndex) => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < loadedChunks.length && loadedChunks[nextIndex]?.audioUrl) {
      const nextAudio = new Audio();
      nextAudio.setAttribute('playsinline', 'true');
      nextAudio.preload = 'auto';
      nextAudio.src = loadedChunks[nextIndex].audioUrl;
      nextAudioRef.current = nextAudio;
      nextAudio.load();
    }
  };

  // Preload UNLOCKED audio chunks (silent, no UI spinner — dashboard handles loading UX)
  const preloadAllChunks = async (chunks, lang) => {
    abortRef.current = false;
    
    // 1/12 DRIP: only preload chunks within unlocked boundary
    const unlockedCount = Math.min(
      unlockedChunkCountRef.current[lang] || chunks.length,
      chunks.length
    );
    const totalChunks = unlockedCount > 0 ? unlockedCount : chunks.length;
    console.log(`[FutureStory] Silent preloading ${totalChunks}/${chunks.length} chunks (1/12 drip) for ${resolvedDomainKey}/${lang}`);
    
    const loadedChunks = [];
    
    for (let i = 0; i < totalChunks; i++) {
      if (abortRef.current) return null;
      
      const chunk = chunks[i];
      const audioUrl = await getAudioForChunk(chunk.text, lang, i);
      
      loadedChunks.push({
        ...chunk,
        audioUrl: audioUrl || null
      });
    }
    
    console.log(`[FutureStory] ${totalChunks} chunks preloaded for ${resolvedDomainKey}/${lang}`);
    return loadedChunks;
  };

  // Play a single preloaded chunk (like PastStory's playChunk)
  const playPreloadedChunk = (chunk, index, allLoadedChunks) => {
    return new Promise((resolve) => {
      if (abortRef.current || !chunk.audioUrl) {
        resolve();
        return;
      }
      
      setCurrentChunkIndex(index);
      // Animation follows currently playing chunk's mahadasha.
      // Overview chunks (mahadashaIndex = -1) keep the highlight on the first future MD (Rahu).
      {
        const mdForChunk = chunk.mahadashaIndex >= 0 ? chunk.mahadashaIndex : 0;
        setCurrentMahadashaIndex(mdForChunk);
      }
      updateDisplayedText(chunk.text);
      
      // Use pre-buffered audio if available
      let audio;
      if (nextAudioRef.current && nextAudioRef.current.src === chunk.audioUrl) {
        audio = nextAudioRef.current;
        nextAudioRef.current = null;
      } else {
        audio = document.getElementById('future-story-audio');
        if (!audio) {
          audio = new Audio();
          audio.setAttribute('playsinline', 'true');
        }
        audio.pause();
        audio.src = chunk.audioUrl;
      }
      
      audioRef.current = audio;
      
      // Start preloading next chunk immediately (seamless)
      if (allLoadedChunks) {
        preloadNextChunk(allLoadedChunks, index);
      }
      
      audio.onended = () => resolve();
      audio.onerror = () => resolve();
      audio.volume = 1.0;
      audio.play().catch(() => resolve());
    });
  };

  // Play all preloaded chunks sequentially (like PastStory's playAllChunks)
  const playAllPreloadedChunks = async (loadedChunks, startFrom = 0) => {
    setIsPlaying(true);
    setIsPaused(false);
    setShowSkipButton(true);
    setStoryComplete(false);
    abortRef.current = false;
    
    // Preload first chunk's next audio
    if (startFrom < loadedChunks.length - 1) {
      preloadNextChunk(loadedChunks, startFrom);
    }
    
    // 1/12 DRIP LOCK: stop playback at the unlocked boundary
    const unlockedCount = Math.min(
      unlockedChunkCountRef.current[currentLanguageRef.current] || loadedChunks.length,
      loadedChunks.length
    );
    const endIdx = unlockedCount > 0 ? unlockedCount : loadedChunks.length;

    for (let i = startFrom; i < endIdx; i++) {
      if (abortRef.current) break;
      
      setPlaybackPosition(prev => ({ ...prev, [currentLanguageRef.current]: i }));
      playbackPositionRef.current[currentLanguageRef.current] = i;
      
      // playPreloadedChunk resolves when audio ends.
      // If paused, audio.pause() keeps it blocked until audio.play() resumes → onended fires.
      await playPreloadedChunk(loadedChunks[i], i, loadedChunks);
    }
    
    if (!abortRef.current) {
      setIsPlaying(false);
      setShowSkipButton(false);
      setStoryComplete(true);
      playbackPositionRef.current[currentLanguageRef.current] = endIdx;
      setPlaybackPosition(prev => ({ ...prev, [currentLanguageRef.current]: endIdx }));
    }
  };

  // Ref to hold preloaded chunks for resume
  const preloadedChunksRef = useRef({ hindi: null, hinglish: null, english: null });

  // Play entire story — FAST PATH (IndexedDB) or PRELOAD PATH (like PastStory)
  // Falls back to Hindi if selected language audio is unavailable
  const playStory = async (lang, startFromIndex = 0) => {
    const chunks = audioChunks[lang];
    
    if (!chunks || chunks.length === 0) {
      console.error('No audio chunks available for', lang);
      return;
    }
    
    stopAllAudio();
    await new Promise(r => setTimeout(r, 50));
    
    setIsPlaying(true);
    setShowSkipButton(true);
    setStoryComplete(false);
    abortRef.current = false;
    currentLanguageRef.current = lang;
    
    // FAST PATH: Try IndexedDB cached full audio first
    let cachedFullAudio = fullAudioCacheRef.current[lang];
    if (!cachedFullAudio) {
      cachedFullAudio = await tryLoadCachedAudio(lang);
    }
    
    // FALLBACK: If selected language has no cached audio, try Hindi (most reliable)
    if (!cachedFullAudio && lang !== 'hindi') {
      console.log(`[FutureStory] No cached audio for ${lang}, trying Hindi fallback...`);
      let hindiAudio = fullAudioCacheRef.current['hindi'];
      if (!hindiAudio) {
        hindiAudio = await tryLoadCachedAudio('hindi');
      }
      if (hindiAudio && audioChunks['hindi']?.length > 0) {
        console.log(`[FutureStory] Using Hindi fallback audio for ${resolvedDomainKey}`);
        currentLanguageRef.current = 'hindi';
        await playFullCachedAudio(hindiAudio, audioChunks['hindi'], 0);
        return;
      }
    }
    
    if (cachedFullAudio) {
      console.log(`[FutureStory] Playing full cached audio for ${resolvedDomainKey}/${lang}`);
      await playFullCachedAudio(cachedFullAudio, chunks, startFromIndex);
      return;
    }
    
    // PRELOAD PATH (like PastStory): Preload ALL chunks first, then play
    // Check if we already preloaded for this language
    let loadedChunks = preloadedChunksRef.current[lang];
    
    if (!loadedChunks || loadedChunks.length !== chunks.length) {
      // Need to preload
      loadedChunks = await preloadAllChunks(chunks, lang);
      if (!loadedChunks || abortRef.current) {
        setIsPlaying(false);
        return;
      }
      preloadedChunksRef.current[lang] = loadedChunks;
    }
    
    // Play all preloaded chunks
    await playAllPreloadedChunks(loadedChunks, startFromIndex);
  };

  // Play/Pause toggle — SIMPLE like PastStory
  const handlePlayPauseToggle = () => {
    // Unlock iOS audio on first tap
    if (!audioUnlockedRef.current) {
      audioUnlockedRef.current = true;
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
          const ctx = new AudioContext();
          const buffer = ctx.createBuffer(1, 1, 22050);
          const source = ctx.createBufferSource();
          source.buffer = buffer;
          source.connect(ctx.destination);
          source.start(0);
          if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        }
      } catch(e) {}
      // Prime audio element
      const audio = document.getElementById('future-story-audio');
      if (audio) {
        audio.src = 'data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYGq6FAAAAAAAAAAAAAAAAAAAAA';
        audio.volume = 1.0;
        audio.play().catch(() => {});
      }
    }
    
    if (isPlaying) {
      // PAUSE — simple like PastStory
      setIsPaused(true);
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setIsPlaying(false);
    } else {
      const chunks = audioChunks[selectedLanguage];
      const totalChunks = chunks?.length || 0;
      
      if (totalChunks === 0) {
        pendingPlayRef.current = true;
        return;
      }
      
      if (storyComplete) {
        // Restart from beginning
        setCurrentChunkIndex(0);
        setCurrentMahadashaIndex(0);
        setStoryComplete(false);
        playbackPositionRef.current[selectedLanguage] = 0;
        setPlaybackPosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
        playStory(selectedLanguage, 0);
      } else if (isPaused && audioRef.current && audioRef.current.paused && audioRef.current.src) {
        // RESUME — simple like PastStory: just resume the audio element
        // The playAllPreloadedChunks loop is blocked waiting for onended, 
        // which fires after audio.play() resumes and finishes
        setIsPaused(false);
        setIsPlaying(true);
        setShowSkipButton(true);
        audioRef.current.play().catch(() => {});
      } else {
        // Fresh play
        const currentPos = playbackPositionRef.current[selectedLanguage] || 0;
        playStory(selectedLanguage, currentPos);
      }
    }
  };

  // Language change — SIMPLE like PastStory: stop, reset, show text
  const handleLanguageChange = (newLang) => {
    if (newLang === selectedLanguage) return;
    
    // Save current language's playback position before switching
    if (audioRef.current) {
      const currentPos = currentChunkIndex;
      playbackPositionRef.current[selectedLanguage] = currentPos;
      const currentAudioTime = audioRef.current.currentTime || 0;
      setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: currentAudioTime }));
    }
    
    // Stop current playback
    abortRef.current = true;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    
    setSelectedLanguage(newLang);
    currentLanguageRef.current = newLang;
    setIsPlaying(false);
    setIsPaused(false);
    setShowSkipButton(false);
    
    // Save language preference
    localStorage.setItem('future_story_language', newLang);
    
    // Restore saved position for this language (if previously played)
    const savedChunkPos = playbackPositionRef.current[newLang];
    const newChunks = audioChunks[newLang];
    
    if (savedChunkPos !== undefined && savedChunkPos > 0 && newChunks && newChunks[savedChunkPos]) {
      // Resume from saved position
      setCurrentChunkIndex(savedChunkPos);
      previousTextRef.current = '';
      updateDisplayedText(newChunks[savedChunkPos].text);
      setCurrentMahadashaIndex(Math.max(0, newChunks[savedChunkPos].mahadashaIndex));
      setStoryComplete(false);
    } else if (newChunks && newChunks.length > 0) {
      // Start from beginning
      setCurrentChunkIndex(0);
      previousTextRef.current = '';
      updateDisplayedText(newChunks[0].text);
      setCurrentMahadashaIndex(Math.max(0, newChunks[0].mahadashaIndex));
      setStoryComplete(false);
      playbackPositionRef.current[newLang] = 0;
      setPlaybackPosition(prev => ({ ...prev, [newLang]: 0 }));
    } else {
      updateDisplayedText('');
      setCurrentMahadashaIndex(-1);
    }
    
    abortRef.current = false;
  };

  // Skip handler
  const handleSkip = () => {
    stopAllAudio();
    const chunks = audioChunks[selectedLanguage];
    setPlaybackPosition(prev => ({ ...prev, [selectedLanguage]: chunks?.length || 0 }));
    setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
    setIsPlaying(false);
    setShowSkipButton(false);
    setStoryComplete(true);
  };

  // Replay handler
  const handleReplay = () => {
    stopAllAudio();
    setPlaybackPosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
    setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: 0 }));
    setCurrentChunkIndex(0);
    setCurrentMahadashaIndex(-1);
    setStoryComplete(false);
    
    setTimeout(() => {
      playStory(selectedLanguage, 0, 0);
    }, 100);
  };

  // Navigate to adjacent domain - PAUSE current and preserve language & position
  const handleDomainSwipe = (direction) => {
    // Save current audio position before stopping
    if (audioRef.current && isPlaying) {
      const currentTime = audioRef.current.currentTime || 0;
      setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: currentTime }));
    }
    
    // Stop current audio
    stopAllAudio(false);
    setIsPlaying(false);
    
    // Save current domain position before navigating
    saveDomainPositions();
    
    // CRITICAL: Clear displayed text immediately to prevent stale content flash
    setDisplayedText('');
    previousTextRef.current = '';
    displayedTextRef.current = '';
    
    // Calculate new domain index (wrap around)
    let newIndex = currentDomainIndex + direction;
    if (newIndex < 0) newIndex = ALL_DOMAINS.length - 1;
    if (newIndex >= ALL_DOMAINS.length) newIndex = 0;
    
    const newDomain = ALL_DOMAINS[newIndex];
    navigate(`/future-story/${newDomain.key}`);
  };

  // Load saved language preference on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('future_story_language');
    if (savedLanguage && LANGUAGES.includes(savedLanguage)) {
      setSelectedLanguage(savedLanguage);
      currentLanguageRef.current = savedLanguage;
    }
  }, []);

  // Check IndexedDB for pre-cached full audio (from PaymentSuccess)
  // Prioritize current language first for fastest first-play
  useEffect(() => {
    const checkCachedAudio = async () => {
      if (!resolvedDomainKey) return;
      const userId = localStorage.getItem('tts_user_id') || localStorage.getItem('user_id') || localStorage.getItem('audio_user_id') || localStorage.getItem('session_id') || localStorage.getItem('taraanaya_device_id') || 'default';
      // Load CURRENT language first for fastest play readiness
      const orderedLangs = [selectedLanguage, ...['hindi', 'hinglish', 'english'].filter(l => l !== selectedLanguage)];
      for (const lang of orderedLangs) {
        if (fullAudioCacheRef.current[lang]) {
          if (lang === selectedLanguage) setFirstChunkReady(true);
          continue;
        }
        try {
          const cached = await getAudioLocal(userId, `domain_${resolvedDomainKey}`, lang);
          if (cached) {
            fullAudioCacheRef.current[lang] = cached;
            // Pre-create blob URL for instant language switching
            fullAudioBlobUrlRef.current[lang] = base64ToAudioUrl(cached);
            if (lang === selectedLanguage) {
              setFirstChunkReady(true);
            }
          }
        } catch (e) { /* IndexedDB might not be available */ }
      }
    };
    checkCachedAudio();
  }, [resolvedDomainKey, selectedLanguage]);

  // Visibility change handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isPlaying) {
        const currentTime = audioRef.current?.currentTime || 0;
        setAudioTimePosition(prev => ({ ...prev, [selectedLanguage]: currentTime }));
        stopAllAudio();
        setIsPlaying(false);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isPlaying, selectedLanguage, stopAllAudio]);

  // Cleanup on unmount - save positions before leaving
  useEffect(() => {
    return () => {
      saveDomainPositions();
      stopAllAudio();
    };
  }, [stopAllAudio, saveDomainPositions]);

  // Save current domain position when domain changes
  const prevDomainRef = useRef(null);
  
  // Load story data on mount or domain change
  useEffect(() => {
    const loadData = async () => {
      // Save previous domain's position before switching
      if (prevDomainRef.current && prevDomainRef.current !== resolvedDomainKey) {
        domainPositionsRef.current[prevDomainRef.current] = {
          playback: { ...playbackPositionRef.current },
          audioTime: { ...audioTimePositionRef.current },
          chunkIndex: currentChunkIndexRef.current,
          mahadashaIndex: currentMahadashaIndexRef.current,
          displayedText: displayedTextRef.current,
          language: selectedLanguage
        };
        // Persist to sessionStorage
        try { sessionStorage.setItem('future_reel_positions', JSON.stringify(domainPositionsRef.current)); } catch(e) {}
      }
      prevDomainRef.current = resolvedDomainKey;
      
      // CRITICAL: Force-clear displayed text on EVERY domain load to prevent stale content
      // This runs synchronously before any async operations
      previousTextRef.current = '';
      isUpdatingTextRef.current = false;
      
      // Only show loading if we DON'T have cached data anywhere
      const hasCachedData = !!storyDataRef.current;
      // Check localStorage for DOMAIN REELS specifically (not just any data)
      let hasReelData = false;
      if (!hasCachedData) {
        try {
          const unified = localStorage.getItem('unified_story_data');
          if (unified) {
            const ud = JSON.parse(unified);
            const reels = ud?.future_story?.domain_reels || {};
            hasReelData = Object.values(reels).some(lr => 
              lr && typeof lr === 'object' && Object.values(lr).some(v => typeof v === 'string' && v.length > 50)
            );
          }
        } catch(e) {}
      }
      if (!hasCachedData && !hasReelData) {
        setIsLoading(true);
      }
      setFirstChunkReady(false);
      
      // Check if we have saved position for this domain
      const savedPos = domainPositionsRef.current[resolvedDomainKey];
      if (savedPos) {
        // Restore saved position
        setPlaybackPosition(savedPos.playback);
        setAudioTimePosition(savedPos.audioTime);
        setCurrentChunkIndex(savedPos.chunkIndex);
        setCurrentMahadashaIndex(Math.max(0, savedPos.mahadashaIndex || 0));
        // Don't restore old displayedText - let loadData set it fresh from chunks
        // This prevents stale domain text from flashing
        playbackPositionRef.current = { ...savedPos.playback };
        audioTimePositionRef.current = { ...savedPos.audioTime };
        // Restore saved language for this domain
        if (savedPos.language && LANGUAGES.includes(savedPos.language)) {
          setSelectedLanguage(savedPos.language);
          currentLanguageRef.current = savedPos.language;
        }
      } else {
        // First time visiting this domain - start fresh
        setPlaybackPosition({ hindi: 0, hinglish: 0, english: 0 });
        setAudioTimePosition({ hindi: 0, hinglish: 0, english: 0 });
        setCurrentChunkIndex(0);
        setCurrentMahadashaIndex(0);
        setDisplayedText('');
        playbackPositionRef.current = { hindi: 0, hinglish: 0, english: 0 };
        audioTimePositionRef.current = { hindi: 0, hinglish: 0, english: 0 };
      }
      setStoryComplete(false);
      audioUrlCacheRef.current = { hindi: {}, hinglish: {}, english: {} };
      // Clear full audio cache refs — they belong to the PREVIOUS domain
      // The checkCachedAudio useEffect will reload for the new domain
      fullAudioCacheRef.current = {};
      fullAudioBlobUrlRef.current = {};
      // Clear preloaded chunks for previous domain
      preloadedChunksRef.current = { hindi: null, hinglish: null, english: null };
      // Reset text refs so updateDisplayedText doesn't skip on re-entry
      previousTextRef.current = '';
      isUpdatingTextRef.current = false;
      
      // If we already have cached story data, skip re-reading localStorage/API
      let unifiedStoryData = storyDataRef.current;
      let birthData = null;
      let aiContentData = null;
      
      // Always parse birthDetails for name/year (lightweight)
      const birthDetails = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
      if (birthDetails) {
        try { birthData = JSON.parse(birthDetails); } catch(e) {}
      }
      
      if (!unifiedStoryData) {
        // First load - get data from localStorage or API
        if (!birthData) {
          navigate('/onboarding-new');
          return;
        }
        
        // Try localStorage first
        const storedData = localStorage.getItem('unified_story_data');
      
        if (storedData) {
          try {
            unifiedStoryData = JSON.parse(storedData);
          } catch (e) {
            console.log('Failed to parse stored data');
          }
        }
      
        // Try AI content data (newer, AI-generated content)
        const aiStored = localStorage.getItem('ai_content_data');
        if (aiStored) {
          try {
            aiContentData = JSON.parse(aiStored);
            console.log('✅ AI content loaded for Future Story:', Object.keys(aiContentData?.future_reels || {}));
          } catch (e) {
            console.log('Failed to parse AI content');
          }
        }
      
        // Try MongoDB if not in localStorage (critical on iOS where localStorage quota exceeded)
        const userId = localStorage.getItem('user_id') || localStorage.getItem('audio_user_id') || localStorage.getItem('session_id') || localStorage.getItem('taraanaya_device_id');
        const deviceId = localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id');
        if (!unifiedStoryData && (userId || deviceId)) {
          try {
            const loadUrl = `${API_URL}/api/unified-story/load/${userId || deviceId}${deviceId ? `?device_id=${deviceId}` : ''}`;
            console.log('[FutureStory] Fetching from MongoDB:', loadUrl);
            const response = await fetch(loadUrl);
            if (response.ok) {
              const result = await response.json();
              if (result.success && result.data) {
                unifiedStoryData = result.data;
                console.log('✅ Loaded unified story from MongoDB fallback');
                // Try to save back to localStorage (may fail on iOS)
                try { localStorage.setItem('unified_story_data', JSON.stringify(unifiedStoryData)); } catch(e) { console.log('localStorage save failed (quota):', e.message); }
              }
            }
          } catch (e) {
            console.log('MongoDB load error:', e.message);
          }
        }
        
        // If domain_reels are missing/empty, fetch fresh from server
        const cacheKey = localStorage.getItem('story_cache_key') || unifiedStoryData?.cache_key;
        const existingReels = unifiedStoryData?.future_story?.domain_reels || {};
        const hasReels = Object.values(existingReels).some(lr => 
          lr && typeof lr === 'object' && Object.values(lr).some(v => typeof v === 'string' && v.length > 50)
        );
        if (!hasReels && cacheKey) {
          console.log('⚡ Domain reels empty in localStorage, fetching from server...');
          try {
            const resp = await fetch(`${API_URL}/api/unified-story/status/${cacheKey}?device_id=${encodeURIComponent(localStorage.getItem("taraanaya_device_id") || localStorage.getItem("device_id") || "")}`);
            if (resp.ok) {
              const serverResult = await resp.json();
              const serverData = serverResult?.data;
              if (serverData) {
                // Update reels from server
                const serverReels = serverData?.future_story?.domain_reels;
                if (serverReels && Object.values(serverReels).some(lr => 
                  lr && typeof lr === 'object' && Object.values(lr).some(v => typeof v === 'string' && v.length > 50)
                )) {
                  if (!unifiedStoryData) unifiedStoryData = {};
                  if (!unifiedStoryData.future_story) unifiedStoryData.future_story = {};
                  unifiedStoryData.future_story.domain_reels = serverReels;
                  console.log('✅ Domain reels loaded from server');
                }
                // Also update mahadashas if missing
                if (serverData.all_mahadashas?.length > 0 && !unifiedStoryData?.all_mahadashas?.length) {
                  unifiedStoryData.all_mahadashas = serverData.all_mahadashas;
                  console.log(`✅ Loaded ${serverData.all_mahadashas.length} mahadashas from server`);
                }
                // Also preserve future_mahadasha_sections from server
                if (serverData.future_mahadasha_sections && Object.keys(serverData.future_mahadasha_sections).length > 0) {
                  unifiedStoryData.future_mahadasha_sections = serverData.future_mahadasha_sections;
                  console.log('✅ Loaded future_mahadasha_sections from server');
                }
                // Persist to localStorage
                localStorage.setItem('unified_story_data', JSON.stringify(unifiedStoryData));
              }
            }
          } catch(e) {
            console.log('Server fetch error:', e.message);
          }
        }
      
        if (!unifiedStoryData && !aiContentData) {
          navigate('/generating-story');
          return;
        }
      
        // Merge AI content into unified story data if available
        if (aiContentData?.future_reels) {
          if (!unifiedStoryData) {
            unifiedStoryData = { user_info: aiContentData.user_info || {} };
          }
        
          // Inject AI future reels into domain_reels structure
          const aiReels = aiContentData.future_reels;
          if (!unifiedStoryData.future_story) {
            unifiedStoryData.future_story = { domain_reels: {} };
          }
        
          // AI reels now come in language-first structure: future_reels.english.health
          // OR old format: future_reels.health (single language)
          for (const lang of ['english', 'hindi', 'hinglish']) {
            if (!unifiedStoryData.future_story.domain_reels[lang]) {
              unifiedStoryData.future_story.domain_reels[lang] = {};
            }
          
            // Check if new format (language-first)
            if (aiReels[lang] && typeof aiReels[lang] === 'object') {
              // New format: future_reels.english.health
              for (const [domain, content] of Object.entries(aiReels[lang])) {
                if (content && typeof content === 'string') {
                  unifiedStoryData.future_story.domain_reels[lang][domain] = content;
                }
              }
            } else {
              // Old format: future_reels.health (use same content for all languages)
              for (const [domain, content] of Object.entries(aiReels)) {
                if (domain !== '_tokens' && domain !== '_language' && content && typeof content === 'string') {
                  unifiedStoryData.future_story.domain_reels[lang][domain] = content;
                }
              }
            }
          }
        
          console.log('✅ AI future reels merged into unified story');
        }
      } // end of if (!unifiedStoryData) - first load block
      
      storyDataRef.current = unifiedStoryData;
      
      // Birth Details = Source of Truth for name
      const userName = birthData.name || 'User';
      setPersonName(userName);
      
      // Get birth year for age calculation
      const bYear = parseInt(unifiedStoryData?.user_info?.birth_date?.split('-')[0]) || 
                    birthData.year || 1997;
      setBirthYear(bYear);
      const currentYear = new Date().getFullYear();
      
      // Get FUTURE mahadashas only (from current year onwards)
      // Check multiple sources for mahadasha data — ROBUST fallback chain
      let allMahadashas = unifiedStoryData?.all_mahadashas || 
                           aiContentData?.all_mahadashas || 
                           unifiedStoryData?.future_story?.mahadashas ||
                           unifiedStoryData?.past_story?.mahadashas ||
                           (unifiedStoryData?.future_mahadashas ? 
                             [...(unifiedStoryData?.past_mahadashas || []), ...(unifiedStoryData?.future_mahadashas || [])] : 
                             []);
      
      // If allMahadashas is still empty, try extracting from story_data wrapper (MongoDB format)
      if ((!allMahadashas || allMahadashas.length === 0) && unifiedStoryData?.story_data) {
        const sd = unifiedStoryData.story_data;
        allMahadashas = sd.all_mahadashas || sd.future_story?.mahadashas || sd.past_story?.mahadashas || [];
        if (allMahadashas.length > 0) {
          console.log(`✅ Found ${allMahadashas.length} mahadashas from story_data wrapper`);
        }
      }
      
      // If still empty, try future_mahadashas directly from unifiedStoryData (newer format)
      if ((!allMahadashas || allMahadashas.length === 0) && unifiedStoryData?.future_mahadashas?.length > 0) {
        allMahadashas = [...(unifiedStoryData.past_mahadashas || []), ...unifiedStoryData.future_mahadashas];
        console.log(`✅ Assembled ${allMahadashas.length} mahadashas from past+future arrays`);
      }
      
      // If mahadashas missing from localStorage, try fetching from server
      if ((!allMahadashas || allMahadashas.length === 0)) {
        const mdCacheKey = localStorage.getItem('story_cache_key') || unifiedStoryData?.cache_key;
        const mdUserId = localStorage.getItem('user_id') || localStorage.getItem('audio_user_id') || localStorage.getItem('session_id');
        
        // Try cache_key first, then user_id load endpoint
        const fetchAttempts = [];
        if (mdCacheKey) fetchAttempts.push(`${API_URL}/api/unified-story/status/${mdCacheKey}?device_id=${encodeURIComponent(localStorage.getItem("taraanaya_device_id") || localStorage.getItem("device_id") || "")}`);
        if (mdUserId) fetchAttempts.push(`${API_URL}/api/unified-story/load/${mdUserId}`);
        
        for (const fetchUrl of fetchAttempts) {
          if (allMahadashas && allMahadashas.length > 0) break;
          try {
            console.log('[FutureStory] Fetching mahadashas from:', fetchUrl);
            const resp = await fetch(fetchUrl);
            if (resp.ok) {
              const serverData = await resp.json();
              const sData = serverData?.data || serverData;
              if (sData?.all_mahadashas?.length > 0) {
                allMahadashas = sData.all_mahadashas;
                // Persist to unifiedStoryData for next time
                if (unifiedStoryData) {
                  unifiedStoryData.all_mahadashas = allMahadashas;
                  try { localStorage.setItem('unified_story_data', JSON.stringify(unifiedStoryData)); } catch(e) {}
                }
                console.log(`✅ Fetched ${allMahadashas.length} mahadashas from server`);
              } else if (sData?.future_story?.mahadashas?.length > 0) {
                allMahadashas = sData.future_story.mahadashas;
                console.log(`✅ Fetched ${allMahadashas.length} mahadashas from server (future_story.mahadashas)`);
              }
            }
          } catch(e) { console.log('Mahadasha fetch error:', e.message); }
        }
      }
      const futureMds = [];
      
      // FAST PATH: If backend gave us future_story.mahadashas directly (already future-only),
      // use them as-is. Only build from all_mahadashas if this direct list is empty.
      const directFutureList = unifiedStoryData?.future_story?.mahadashas || 
                               unifiedStoryData?.future_mahadashas ||
                               unifiedStoryData?.story_data?.future_story?.mahadashas;
      const useDirectList = Array.isArray(directFutureList) && directFutureList.length > 0;
      const sourceList = useDirectList ? directFutureList : allMahadashas;
      
      sourceList.forEach(md => {
        // Robust year extraction — support multiple backend field shapes
        let startYear = 0, endYear = 0;
        if (md.years && typeof md.years === 'string' && md.years.includes('-')) {
          const yp = md.years.split('-');
          startYear = parseInt(yp[0]) || 0;
          endYear = parseInt(yp[1]) || 0;
        } else if (md.start_year && md.end_year) {
          startYear = parseInt(md.start_year) || 0;
          endYear = parseInt(md.end_year) || 0;
        } else if (typeof md.start_age === 'number' && typeof md.end_age === 'number' && bYear) {
          startYear = bYear + md.start_age;
          endYear = bYear + md.end_age;
        } else if (md.start_date && md.end_date) {
          startYear = parseInt(String(md.start_date).split('-')[0]) || 0;
          endYear = parseInt(String(md.end_date).split('-')[0]) || 0;
        }
        
        // When using direct future list, skip the endYear check (server already filtered).
        // Otherwise, only include if end year is in future.
        const shouldInclude = useDirectList ? true : (endYear >= currentYear);
        if (shouldInclude) {
          const isCurrent = md.status === 'current' || md.is_current || (startYear <= currentYear && endYear >= currentYear);
          const displayStartYear = isCurrent ? currentYear : Math.max(startYear, currentYear);
          
          // Calculate age range
          const startAge = displayStartYear - bYear;
          const endAge = endYear - bYear;
          
          futureMds.push({
            ...md,
            years: (startYear && endYear) ? `${displayStartYear}-${endYear}` : (md.years || ''),
            age_range: (startYear && endYear) ? `${startAge}-${endAge}` : (md.age_range || ''),
            start_age: startAge || md.start_age || 0,
            end_age: endAge || md.end_age || 0,
            is_current: isCurrent,
            color: PLANET_COLORS[md.lord] || '#edc161',
            icon: PLANET_ICONS[md.lord] || '☉'
          });
        }
      });
      
      console.log(`🕐 Future mahadashas built: ${futureMds.length} from ${sourceList.length} total (useDirectList=${useDirectList})`);
      
      // FINAL FALLBACK: If futureMds still empty but we have ANY mahadashas, use them.
      // User explicitly requested: timeline must render. Previous commit hid timeline behind
      // futureMahadashas.length > 0 guard — this fallback guarantees timeline is visible.
      if (futureMds.length === 0 && (allMahadashas?.length > 0 || sourceList?.length > 0)) {
        const src = sourceList?.length > 0 ? sourceList : allMahadashas;
        src.slice(-6).forEach(md => {
          futureMds.push({
            ...md,
            years: md.years || (md.start_age && md.end_age && bYear ? `${bYear + md.start_age}-${bYear + md.end_age}` : ''),
            age_range: md.age_range || (md.start_age && md.end_age ? `${md.start_age}-${md.end_age}` : ''),
            start_age: md.start_age || 0,
            end_age: md.end_age || 0,
            is_current: md.status === 'current' || md.is_current || false,
            color: PLANET_COLORS[md.lord] || '#edc161',
            icon: PLANET_ICONS[md.lord] || '☉'
          });
        });
        console.log(`🕐 Fallback: using last ${futureMds.length} mahadashas to guarantee timeline renders`);
      }
      
      // LAST-RESORT FALLBACK: No mahadashas anywhere → compute fresh from birth data via backend
      if (futureMds.length === 0 && birthData) {
        try {
          console.log('🕐 No mahadashas anywhere — fetching fresh from /api/hybrid-story/mahadasha-timeline');
          const mdResp = await fetch(`${API_URL}/api/hybrid-story/mahadasha-timeline`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              birth_data: {
                name: birthData.name || 'User',
                year: parseInt(birthData.year) || 1997,
                month: parseInt(birthData.month) || 1,
                day: parseInt(birthData.day) || 1,
                hour: parseInt(birthData.hour) || 12,
                minute: parseInt(birthData.minute) || 0,
                latitude: parseFloat(birthData.latitude) || 28.6139,
                longitude: parseFloat(birthData.longitude) || 77.209,
                timezone_offset: parseFloat(birthData.timezone_offset) || 5.5,
                gender: birthData.gender || 'male'
              }
            })
          });
          if (mdResp.ok) {
            const mdData = await mdResp.json();
            if (mdData?.success) {
              const current = mdData.current_running ? [mdData.current_running] : [];
              const upcoming = mdData.upcoming || [];
              const combined = [...current, ...upcoming].slice(0, 6);
              combined.forEach(md => {
                futureMds.push({
                  ...md,
                  years: md.years || (md.start_age && md.end_age && bYear ? `${bYear + md.start_age}-${bYear + md.end_age}` : ''),
                  age_range: md.age_range || (md.start_age && md.end_age ? `${md.start_age}-${md.end_age}` : ''),
                  start_age: md.start_age || 0,
                  end_age: md.end_age || 0,
                  is_current: md.status === 'current' || md.is_current || false,
                  color: PLANET_COLORS[md.lord] || '#edc161',
                  icon: PLANET_ICONS[md.lord] || '☉'
                });
              });
              console.log(`🕐 Fetched ${futureMds.length} mahadashas from /hybrid-story/mahadasha-timeline`);
              // Cache into unifiedStoryData for next time
              if (unifiedStoryData && futureMds.length > 0) {
                try {
                  if (!unifiedStoryData.future_story) unifiedStoryData.future_story = {};
                  unifiedStoryData.future_story.mahadashas = futureMds;
                  localStorage.setItem('unified_story_data', JSON.stringify(unifiedStoryData));
                } catch(e) { /* quota ok */ }
              }
            }
          }
        } catch(e) { console.log('[FutureStory] mahadasha-timeline fetch error:', e.message); }
      }
      
      // Limit to 6-7 mahadashas for clean timeline
      const limitedFutureMds = futureMds.slice(0, 6);
      setFutureMahadashas(limitedFutureMds);
      
      // Get domain-specific future story
      const domainReels = unifiedStoryData?.future_story?.domain_reels || {};
      const currentKey = resolvedDomainKey || 'health';
      const domainKeyForBackend = currentKey === 'spiritual' ? 'spirituality' : currentKey;
      
      // Prepare audio chunks for all languages
      const newAudioChunks = { hindi: [], hinglish: [], english: [] };
      
      // PRIMARY: Use future_mahadasha_sections (per-mahadasha per-domain text)
      // This guarantees correct sequence: Venus health → Sun health → Moon health → Mars health → ...
      const futureSections = unifiedStoryData?.future_mahadasha_sections || {};
      
      console.log(`🔍 Loading domain: ${currentKey}, using future_mahadasha_sections`);
      
      // Check if we have per-mahadasha domain_impacts
      const hasMahadashaSections = LANGUAGES.some(lang => {
        const sections = futureSections[lang] || [];
        return sections.some(md => md?.domain_impacts?.[currentKey]?.length > 20 || md?.domain_impacts?.[domainKeyForBackend]?.length > 20);
      });
      
      for (const lang of LANGUAGES) {
        let fullText = '';
        
        if (hasMahadashaSections) {
          // BUILD CHUNKS FROM MAHADASHA SECTIONS (RELIABLE - no guessing needed)
          const sections = futureSections[lang] || [];
          
          // Add intro text if available
          const intro = unifiedStoryData?.future_story?.intro?.[lang] || '';
          if (intro && intro.length > 30) {
            const introClean = cleanNarrationText(intro);
            // OBSERVABILITY: log first 60 chars + version so Railway logs prove
            // that the user's device received the new "Namaste {name} ji" greeting
            // (or any future tone update) post deploy. Search for this event in
            // production logs to verify cache invalidation worked end-to-end.
            try {
              const cv = JSON.parse(localStorage.getItem('content_versions') || '{}');
              rlog('narration_intro_received', {
                domain: currentKey,
                lang,
                first_60: introClean.slice(0, 60),
                starts_with_namaste: /^नमस्ते|^Namaste/i.test(introClean.trim()),
                has_ji: /\b(ji|जी)\b/.test(introClean.slice(0, 80)),
                future_reels_version: cv.future_reels || null,
                story_version: cv.past_story || null,
              });
            } catch {}
            fullText += introClean + '\n\n';
            const introChunks = splitTextIntoChunks(introClean, MAX_CHUNK_CHARS);
            introChunks.forEach(chunkText => {
              newAudioChunks[lang].push({
                text: chunkText, index: newAudioChunks[lang].length,
                mahadashaIndex: -1, type: 'intro', isPart1: true
              });
            });
          }
          
          // Add per-mahadasha domain-specific content in order
          limitedFutureMds.forEach((md, mdIdx) => {
            const lord = md.lord;
            // Find matching section by lord name
            const section = sections.find(s => s.lord === lord) || sections[mdIdx];
            const domainText = section?.domain_impacts?.[currentKey] || section?.domain_impacts?.[domainKeyForBackend] || '';
            
            if (domainText && domainText.length > 20) {
              const cleaned = cleanNarrationText(domainText);
              fullText += cleaned + '\n\n';
              const chunks = splitTextIntoChunks(cleaned, MAX_CHUNK_CHARS);
              chunks.forEach(chunkText => {
                newAudioChunks[lang].push({
                  text: chunkText, index: newAudioChunks[lang].length,
                  mahadashaIndex: mdIdx, type: 'mahadasha', isPart1: false
                });
              });
              console.log(`✅ ${lang} ${currentKey}: ${lord} → ${cleaned.length} chars, mdIdx=${mdIdx}`);
            } else {
              // Fallback to narrative if domain_impacts missing for this mahadasha
              const narrative = section?.narrative || '';
              if (narrative && narrative.length > 30) {
                const cleaned = cleanNarrationText(narrative);
                fullText += cleaned + '\n\n';
                const chunks = splitTextIntoChunks(cleaned, MAX_CHUNK_CHARS);
                chunks.forEach(chunkText => {
                  newAudioChunks[lang].push({
                    text: chunkText, index: newAudioChunks[lang].length,
                    mahadashaIndex: mdIdx, type: 'mahadasha', isPart1: false
                  });
                });
                console.log(`⚠️ ${lang} ${currentKey}: ${lord} → narrative fallback ${cleaned.length} chars`);
              }
            }
          });
          
          fullStoryTextRef.current[lang] = fullText;
          console.log(`📝 ${lang} ${currentKey}: Built ${newAudioChunks[lang].length} chunks from mahadasha sections`);
          
        } else {
          // FALLBACK: Use domain_reels (single text per domain) with paragraph matching
          const domainReels = unifiedStoryData?.future_story?.domain_reels || {};
          const langReels = domainReels[lang] || {};
          const actualReels = langReels.reels || langReels;
          const rawStoryText = actualReels[currentKey] || actualReels[domainKeyForBackend] || '';
          const storyText = cleanNarrationText(rawStoryText);
          fullStoryTextRef.current[lang] = storyText;
        
        // Domain content validation - detect if wrong domain content is loaded
        const domainKeywords = {
          'health': ['health', 'vitality', 'disease', 'body', 'medical', 'swasthya', 'sehat'],
          'career': ['career', 'job', 'profession', 'work', 'business', 'kaam', 'naukri'],
          'marriage': ['love', 'marriage', 'partner', 'spouse', 'relationship', 'pyar', 'shaadi', 'vivah'],
          'wealth': ['wealth', 'money', 'finance', 'income', 'property', 'dhan', 'paisa'],
          'family': ['family', 'children', 'parents', 'siblings', 'home', 'parivar'],
          'education': ['education', 'learning', 'study', 'knowledge', 'academic', 'shiksha', 'padhai'],
          'spiritual': ['spiritual', 'dharma', 'meditation', 'moksha', 'adhyatm'],
          'travel': ['travel', 'foreign', 'abroad', 'journey', 'yatra', 'videsh']
        };
        if (storyText && lang === 'hinglish') {
          const textLower = storyText.substring(0, 500).toLowerCase();
          const expectedKeywords = domainKeywords[currentKey] || [];
          const hasExpectedContent = expectedKeywords.some(kw => textLower.includes(kw));
          if (!hasExpectedContent && storyText.length > 100) {
            console.warn(`⚠️ DOMAIN MISMATCH: ${currentKey} content may contain wrong domain text. First 200 chars:`, storyText.substring(0, 200));
          }
        }
        
        console.log(`📝 ${lang} ${currentKey}: ${storyText.length} chars`);
        
        if (storyText) {
          // SMART SPLITTING: Split by year ranges (20XX-20XX:) to create proper paragraphs
          // This handles cases where all Mahadasha predictions are in one long paragraph
          let splitParagraphs = [];
          
          // First, check if text has year range patterns
          const yearRangePattern = /(\d{4}\s*(-|se|to)\s*\d{4}[^:]*:)/g;
          const hasYearRanges = storyText.match(yearRangePattern);
          
          if (hasYearRanges && hasYearRanges.length >= 3) {
            // Split by year ranges - each becomes a separate chunk for timeline sync
            const parts = storyText.split(/(?=\d{4}\s*(?:-|se|to)\s*\d{4})/);
            
            parts.forEach((part, i) => {
              const trimmed = part.trim();
              if (trimmed.length > 20) {
                splitParagraphs.push(trimmed);
              }
            });
            
            console.log(`📊 ${lang} ${currentKey}: Split into ${splitParagraphs.length} parts by year ranges`);
          } else {
            // Fallback to double-newline splitting
            splitParagraphs = storyText.split(/\n\n+/).filter(p => p.trim().length > 30);
          }
          
          const paragraphs = splitParagraphs.length > 0 ? splitParagraphs : [storyText];
          
          // Find where mahadasha predictions actually start
          // Look for STRUCTURED section headers like **Planet Mahadasha (YYYY-YYYY)**
          // NOT just keyword mentions in overview paragraphs
          let mahadashaStartIdx = -1;
          paragraphs.forEach((para, idx) => {
            if (mahadashaStartIdx === -1) {
              // PRIMARY: Look for markdown-style headers with year ranges (most reliable)
              // These are the actual mahadasha section headers, not overview mentions
              if (para.match(/^\*\*\w+\s+(Mahadasha|mahadasha|महादशा)\s*\(/)) {
                mahadashaStartIdx = idx;
              }
            }
          });
          
          // FALLBACK: If no structured headers found, use keyword detection
          if (mahadashaStartIdx === -1) {
            paragraphs.forEach((para, idx) => {
              if (mahadashaStartIdx === -1) {
                const lowerPara = para.toLowerCase();
                // Check for year range at START of paragraph (not mid-text mentions)
                if (para.match(/^\d{4}\s*(-|se|to)\s*\d{4}/) ||
                    lowerPara.match(/^(ab aapki|now your|looking ahead|अब आपकी)/i)) {
                  mahadashaStartIdx = idx;
                }
              }
            });
          }
          
          // LAST RESORT: If still nothing, assume last 60% is mahadasha content
          if (mahadashaStartIdx === -1) {
            mahadashaStartIdx = Math.floor(paragraphs.length * 0.4);
          }
          
          console.log(`📊 ${lang} ${currentKey}: ${paragraphs.length} paras, mahadasha starts at para ${mahadashaStartIdx}`);
          
          let currentMdIdx = -1;  // Track which mahadasha we're in
          
          paragraphs.forEach((para, idx) => {
            const chunks = splitTextIntoChunks(para, MAX_CHUNK_CHARS);
            const isIntro = idx < mahadashaStartIdx;
            
            // When we enter mahadasha section, start incrementing mahadasha index
            // Detect mahadasha markers: year ranges (2026-2027:), planet names, or "mahadasha" keyword
            if (!isIntro) {
              const lowerPara = para.toLowerCase();
              
              // Check for year range patterns: "2026-2027", "2027 se 2033", "2027 to 2033", "2027 tak", "2027 ke baad"
              const yearRangeMatch = para.match(/\b(20\d{2})\s*(-|se|to|ke\s+baad|tak|until)\s*(20\d{2})/i);
              // Also try to find ANY two year numbers close together
              const allYears = [...para.matchAll(/\b(20\d{2})\b/g)].map(m => parseInt(m[1]));
              
              let matchedIdx = -1;
              
              // Method 1: Direct year range match
              if (yearRangeMatch) {
                const textStartYear = parseInt(yearRangeMatch[1]);
                const textEndYear = parseInt(yearRangeMatch[3]);
                
                limitedFutureMds.forEach((md, mdIdx) => {
                  if (md.years) {
                    const mdYears = md.years.match(/(\d{4})/g);
                    if (mdYears && mdYears.length >= 2) {
                      const mdStart = parseInt(mdYears[0]);
                      const mdEnd = parseInt(mdYears[1]);
                      if (textStartYear >= mdStart - 1 && textStartYear <= mdEnd + 1) {
                        matchedIdx = mdIdx;
                      }
                    }
                  }
                });
              }
              
              // Method 2: Multiple years in text — find best mahadasha match
              if (matchedIdx < 0 && allYears.length >= 2) {
                const textStart = Math.min(...allYears);
                limitedFutureMds.forEach((md, mdIdx) => {
                  if (md.years) {
                    const mdYears = md.years.match(/(\d{4})/g);
                    if (mdYears && mdYears.length >= 2) {
                      const mdStart = parseInt(mdYears[0]);
                      const mdEnd = parseInt(mdYears[1]);
                      if (textStart >= mdStart - 1 && textStart <= mdEnd + 1) {
                        matchedIdx = mdIdx;
                      }
                    }
                  }
                });
              }
              
              // Method 3: Single year in text
              if (matchedIdx < 0 && allYears.length === 1) {
                const yr = allYears[0];
                limitedFutureMds.forEach((md, mdIdx) => {
                  if (md.years) {
                    const mdYears = md.years.match(/(\d{4})/g);
                    if (mdYears && mdYears.length >= 2) {
                      const mdStart = parseInt(mdYears[0]);
                      const mdEnd = parseInt(mdYears[1]);
                      if (yr >= mdStart && yr <= mdEnd) {
                        matchedIdx = mdIdx;
                      }
                    }
                  }
                });
              }
              
              // Method 4: Planet name detection with flexible dasha keywords
              if (matchedIdx < 0) {
                const planetNames = {
                  'sun': 'Sun', 'surya': 'Sun', 'soorya': 'Sun',
                  'moon': 'Moon', 'chandra': 'Moon', 'chand': 'Moon',
                  'mars': 'Mars', 'mangal': 'Mars', 'kuja': 'Mars',
                  'mercury': 'Mercury', 'budh': 'Mercury', 'budha': 'Mercury',
                  'jupiter': 'Jupiter', 'guru': 'Jupiter', 'brihaspati': 'Jupiter',
                  'venus': 'Venus', 'shukra': 'Venus', 'shukr': 'Venus',
                  'saturn': 'Saturn', 'shani': 'Saturn', 'sani': 'Saturn',
                  'rahu': 'Rahu', 'raahu': 'Rahu',
                  'ketu': 'Ketu', 'kethu': 'Ketu'
                };
                
                // Match planet name with any dasha-related keyword (not just "mahadasha")
                const dashaKeywords = ['mahadasha', 'mahaadasha', 'maha dasha', 'dasha', 'ki dasha', 'ka dasha', 'period', 'ka samay', 'ke samay', 'ka prabhav', 'ke prabhav', 'की दशा', 'महादशा', 'का प्रभाव', 'का समय'];
                const hasDashaKeyword = dashaKeywords.some(kw => lowerPara.includes(kw));
                
                for (const [keyword, planet] of Object.entries(planetNames)) {
                  if (lowerPara.includes(keyword) && hasDashaKeyword) {
                    limitedFutureMds.forEach((md, mdIdx) => {
                      if (md.lord === planet) {
                        matchedIdx = mdIdx;
                      }
                    });
                    if (matchedIdx >= 0) break;
                  }
                }
              }
              
              // Method 5: Planet name only (no dasha keyword needed) - last resort
              // If the paragraph prominently mentions a planet, assume it's about that mahadasha
              if (matchedIdx < 0) {
                const planetKeywords = [
                  ['sun', 'surya', 'soorya'], ['moon', 'chandra', 'chand'],
                  ['mars', 'mangal'], ['mercury', 'budh', 'budha'],
                  ['jupiter', 'guru', 'brihaspati'], ['venus', 'shukra', 'shukr'],
                  ['saturn', 'shani', 'sani'], ['rahu', 'raahu'], ['ketu', 'kethu']
                ];
                const planetLords = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
                
                for (let pi = 0; pi < planetKeywords.length; pi++) {
                  const found = planetKeywords[pi].some(kw => {
                    // Check word boundary to avoid partial matches (e.g. "guru" in "gurukul")
                    const regex = new RegExp(`\\b${kw}\\b`, 'i');
                    return regex.test(para);
                  });
                  if (found) {
                    const planet = planetLords[pi];
                    limitedFutureMds.forEach((md, mdIdx) => {
                      if (md.lord === planet) {
                        matchedIdx = mdIdx;
                      }
                    });
                    if (matchedIdx >= 0) {
                      console.log(`🔍 Method 5 planet-only match: ${planet} → MD ${matchedIdx}`);
                      break;
                    }
                  }
                }
              }
              
              if (matchedIdx >= 0) {
                currentMdIdx = matchedIdx;
                console.log(`📍 Matched to Mahadasha ${matchedIdx}: ${limitedFutureMds[matchedIdx]?.lord}`);
              }
            }
            
            // Ensure we have at least index 0 for non-intro content
            if (!isIntro && currentMdIdx < 0) {
              currentMdIdx = 0;
            }
            
            chunks.forEach((chunkText) => {
              newAudioChunks[lang].push({
                text: chunkText,
                index: newAudioChunks[lang].length,
                mahadashaIndex: isIntro ? -1 : Math.max(0, currentMdIdx),
                type: isIntro ? 'intro' : 'mahadasha',
                isPart1: isIntro
              });
            });
          });
        }
        } // end else (domain_reels fallback)
      }
      
      // NOTE: Do NOT setAudioChunks(newAudioChunks) here — wait for drip filter below
      
      // === 12-MONTH DRIP: Lock content by 1/12th of TOTAL narration text ===
      // Total narration (all future mahadashas) is split into 12 equal parts by
      // character count. Each month unlocks +1/12 of the total.
      // IMPORTANT: We keep ALL chunks in audioChunks (needed for correct audio
      // timing calculation), but track an `unlockedChunkCount` so playback stops
      // at the 1/12 boundary and locked chunks never display.
      const currentUnlocked = getUnlockedMonths();
      setUnlockedMonths(currentUnlocked);
      setDaysUntilUnlock(getDaysUntilNextUnlock());
      
      // Compute unlocked chunk count per language (how many WHOLE chunks fall within 1/12 slice)
      const computeUnlockedCount = (langChunks) => {
        const intro = langChunks.filter(c => c.mahadashaIndex === -1);
        const mds = langChunks.filter(c => c.mahadashaIndex !== -1);
        if (mds.length === 0) return intro.length;

        const totalChars = mds.reduce((s, c) => s + (c.text?.length || 0), 0);
        const unlockedChars = Math.max(1, Math.ceil(totalChars * currentUnlocked / 12));

        let accumulated = 0;
        let count = 0;
        for (const c of mds) {
          const len = c.text?.length || 0;
          if (accumulated + len > unlockedChars && count > 0) break;
          count += 1;
          accumulated += len;
          if (accumulated >= unlockedChars) break;
        }
        return intro.length + count;
      };

      const unlockedCounts = {
        hindi: computeUnlockedCount(newAudioChunks.hindi),
        hinglish: computeUnlockedCount(newAudioChunks.hinglish),
        english: computeUnlockedCount(newAudioChunks.english),
      };

      // Store ALL chunks (for audio timing) — playback engine will stop at unlockedCount boundary
      setAudioChunks(newAudioChunks);
      unlockedChunkCountRef.current = unlockedCounts;

      // Rebuild fullStoryTextRef from ONLY UNLOCKED chunks (text shown should be in-sync with audio slice)
      for (const lang of LANGUAGES) {
        const unlockedText = newAudioChunks[lang].slice(0, unlockedCounts[lang]).map(c => c.text).join('\n\n');
        fullStoryTextRef.current[lang] = unlockedText;
      }

      // Compute which mahadasha indices have at least 1 chunk in the 1/12 slice
      // (these must NOT be shown as locked — narration actually covers them this month)
      const primaryLang = selectedLanguage || 'hinglish';
      const primarySlice = newAudioChunks[primaryLang].slice(0, unlockedCounts[primaryLang] || 0);
      const mdIdxSet = new Set();
      let firstSliceMdIdx = -1;
      for (const c of primarySlice) {
        if (c.mahadashaIndex >= 0) {
          mdIdxSet.add(c.mahadashaIndex);
          if (firstSliceMdIdx === -1) firstSliceMdIdx = c.mahadashaIndex;
        }
      }
      // Guarantee: the first mahadasha of the unlock timeline is always "active" for Month 1.
      // Fallback when chunks are all intro (no mahadasha-labeled): active = index 0 (Rahu),
      // and include that index in the unlocked set so it isn't shown as locked.
      const resolvedActiveIdx = firstSliceMdIdx >= 0 ? firstSliceMdIdx : 0;
      mdIdxSet.add(resolvedActiveIdx);
      setUnlockedMdIndices(Array.from(mdIdxSet).sort((a, b) => a - b));
      setActiveDripMdIdx(resolvedActiveIdx);
      
      const totalCharsHinglish = newAudioChunks.hinglish.reduce((s, c) => s + (c.text?.length || 0), 0);
      const unlockedCharsHinglish = newAudioChunks.hinglish.slice(0, unlockedCounts.hinglish).reduce((s, c) => s + (c.text?.length || 0), 0);
      console.log(`🔒 Drip: Month ${currentUnlocked}/12. Hinglish ${unlockedCharsHinglish}/${totalCharsHinglish} chars (${Math.round(unlockedCharsHinglish/Math.max(1,totalCharsHinglish)*100)}%). Chunks unlocked: ${unlockedCounts.hinglish}/${newAudioChunks.hinglish.length}`);
      
      // Set initial text - ALWAYS update for the current domain
      const initialLang = selectedLanguage || 'hinglish';
      const allInitialChunks = newAudioChunks[initialLang] || [];
      const unlockedInitialCount = unlockedCounts[initialLang] || 0;
      const domainChunks = allInitialChunks.slice(0, unlockedInitialCount);
      
      if (domainChunks && domainChunks.length > 0) {
        // Force update displayed text with new domain's content
        previousTextRef.current = ''; // Reset guard to allow update
        updateDisplayedText(domainChunks[0].text);
        // Initial highlight: first chunk's mahadasha (or first future MD=0 for overview/intro)
        const initialActiveMd = domainChunks[0].mahadashaIndex >= 0 ? domainChunks[0].mahadashaIndex : 0;
        setCurrentMahadashaIndex(initialActiveMd);
        console.log(`✅ Domain ${currentKey}: ${unlockedInitialCount}/${allInitialChunks.length} chunks unlocked for ${initialLang} (initial MD: ${initialActiveMd})`);
      } else {
        // No content for this domain - show clear message
        const domainLabel = ALL_DOMAINS.find(d => d.key === currentKey)?.fullName || currentKey;
        const noContentMsg = `${domainLabel} content is being prepared. Please check back shortly.`;
        previousTextRef.current = ''; // Reset guard
        updateDisplayedText(noContentMsg);
        setCurrentMahadashaIndex(0);
        console.log(`⚠️ Domain ${currentKey}: No content available for ${initialLang}`);
      }
      
      // Show content IMMEDIATELY - don't wait for audio pre-fetch
      setIsLoading(false);
      
      // Signal autoplay after data loads
      autoplayPendingRef.current = true;
      
      // Pre-fetch first chunk audio in background (non-blocking)
      if (domainChunks[0]) {
        getAudioForChunk(domainChunks[0].text, initialLang, 0)
          .then((url) => {
            if (url) {
              setFirstChunkReady(true);
              console.log('First chunk audio pre-fetched (background)');
            }
          })
          .catch((e) => console.log('First chunk prefetch failed:', e.message));
      }
    };
    
    loadData().catch(err => {
      console.error('loadData error:', err);
      setIsLoading(false); // Always clear loading screen
    });
    
    // Safety: force clear loading after 5 seconds
    const safetyTimeout = setTimeout(() => setIsLoading(false), 5000);
    return () => clearTimeout(safetyTimeout);
  }, [navigate, resolvedDomainKey, updateDisplayedText]);

  // Autoplay effect - triggers after audioChunks are populated
  // Pre-fetch ALL audio chunks so Play is instant (no TTS delay)
  // Track which domain these chunks belong to, to prevent stale-domain pre-fetch
  const chunksForDomainRef = useRef(null);
  
  useEffect(() => {
    if (!isLoading && !isPlaying) {
      const lang = selectedLanguage || 'hinglish';
      const chunks = audioChunks[lang];
      if (chunks && chunks.length > 0) {
        if (autoplayPendingRef.current) {
          autoplayPendingRef.current = false;
          // Show first chunk text immediately - force reset guard
          previousTextRef.current = '';
          updateDisplayedText(chunks[0].text);
          setCurrentMahadashaIndex(Math.max(0, chunks[0].mahadashaIndex));
          console.log(`Reel ready with ${chunks.length} chunks for ${resolvedDomainKey}. Pre-fetching ALL audio...`);
        }
        
        // Only pre-fetch if domain hasn't changed since chunks were set
        const currentDomainForFetch = resolvedDomainKey;
        chunksForDomainRef.current = currentDomainForFetch;
        
        // Pre-fetch ALL chunks in sequence (TTS API has rate limits)
        // SKIP if full cached audio is available from IndexedDB
        const prefetchAll = async () => {
          // If we already have full cached audio for this language, skip TTS pre-fetch
          if (fullAudioCacheRef.current[lang]) {
            console.log(`[FutureStory] Full cached audio available for ${lang}/${currentDomainForFetch} — skipping TTS pre-fetch`);
            setFirstChunkReady(true);
            if (pendingPlayRef.current && chunksForDomainRef.current === currentDomainForFetch) {
              pendingPlayRef.current = false;
              const currentPos = playbackPositionRef.current[lang] || 0;
              const savedTime = audioTimePositionRef.current[lang] || 0;
              playStory(lang, currentPos, savedTime);
            }
            return;
          }
          for (let i = 0; i < chunks.length; i++) {
            // Stop pre-fetching if domain changed
            if (chunksForDomainRef.current !== currentDomainForFetch) {
              console.log(`Pre-fetch aborted: domain changed from ${currentDomainForFetch}`);
              break;
            }
            try {
              await getAudioForChunk(chunks[i].text, lang, i);
              if (i === 0) {
                setFirstChunkReady(true);
                // If user pressed Play while pre-fetching, start playback now
                if (pendingPlayRef.current && chunksForDomainRef.current === currentDomainForFetch) {
                  pendingPlayRef.current = false;
                  const currentPos = playbackPositionRef.current[lang] || 0;
                  const savedTime = audioTimePositionRef.current[lang] || 0;
                  playStory(lang, currentPos, savedTime);
                }
              }
            } catch(e) {}
          }
          if (chunksForDomainRef.current === currentDomainForFetch) {
            console.log(`All ${chunks.length} audio chunks pre-fetched for ${lang} (${currentDomainForFetch})`);
          }
        };
        prefetchAll();
      }
    }
  }, [audioChunks, isLoading]);


  // Loading screen - only for first-time data load, NOT for domain/language switching
  // Dashboard play button already blocks navigation while reels generate
  if (isLoading) {
    // If reels are still generating in background, redirect to dashboard
    const generating = localStorage.getItem('future_reels_generating') === 'true';
    if (generating) {
      navigate('/dashboard');
      return null;
    }
    // Keep same layout structure during loading to avoid UI distortion on swipe
    return (
      <div style={{
        height: '100dvh',
        maxHeight: '100dvh',
        background: 'radial-gradient(ellipse at center, rgba(237, 193, 97, 0.08) 0%, #020712 100%)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        touchAction: 'none',
        padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)'
      }}>
        {/* Same top section as main UI */}
        <div style={{ flexShrink: 0, paddingTop: '6px', paddingBottom: '2px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 16px', marginBottom: '4px' }}>
            <motion.button onClick={() => navigate('/dashboard')} whileTap={{ scale: 0.9 }} style={{ background: 'rgba(237,193,97,0.1)', border: '1px solid rgba(237,193,97,0.3)', borderRadius: '20px', padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ChevronLeft size={14} style={{ color: '#edc161' }} />
              <span style={{ fontSize: '11px', color: '#edc161' }}>Dashboard</span>
            </motion.button>
            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{currentDomainIndex + 1}/{ALL_DOMAINS.length}</span>
          </div>
          <p style={{ textAlign: 'center', fontSize: 'clamp(14px, 4vw, 18px)', fontWeight: '600', color: 'rgba(255,255,255,0.95)', margin: 0 }}>
            Loading...
          </p>
        </div>
        {/* Sage area - same height as main */}
        <div style={{ flexShrink: 0, height: 'clamp(100px, 18vh, 140px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <motion.img
            src="/sage-final.png"
            alt="Sage"
            animate={{ y: [0, -2, 0], filter: 'drop-shadow(0 0 12px rgba(237, 193, 97, 0.4))' }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            style={{ width: 'clamp(60px, 15vw, 80px)', height: 'auto', opacity: 0.6 }}
          />
        </div>
        {/* Text area placeholder */}
        <div style={{ flex: 1, padding: '0 16px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, background: 'rgba(237,193,97,0.05)', borderRadius: '14px', border: '1px solid rgba(237,193,97,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <motion.div animate={{ opacity: [0.3, 0.7, 0.3] }} transition={{ duration: 1.5, repeat: Infinity }} style={{ fontSize: '13px', color: 'rgba(237,193,97,0.5)' }}>
              Preparing your reel...
            </motion.div>
          </div>
        </div>
        {/* Bottom spacing */}
        <div style={{ flexShrink: 0, height: '80px' }} />
      </div>
    );
  }

  const currentMahadasha = futureMahadashas[currentMahadashaIndex] || {};

  return (
    <div 
      ref={containerRef}
      style={{
        height: '100dvh',
        maxHeight: '100dvh',
        background: 'radial-gradient(ellipse at center, rgba(237, 193, 97, 0.08) 0%, #020712 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)',
      }}
    >
      {/* Persistent audio element - same approach as PastStory for reliable mobile playback */}
      <audio 
        id="future-story-audio"
        playsInline
        webkit-playsinline="true"
        preload="auto"
        style={{ display: 'none' }}
      />
      {/* Stars background */}
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'radial-gradient(2px 2px at 20% 30%, white, transparent), radial-gradient(1px 1px at 60% 70%, white, transparent)',
        backgroundSize: '100vw 100vh',
        opacity: 0.3,
        pointerEvents: 'none'
      }} />

      {/* ===== TOP SECTION: Navigation + Language + Title ===== */}
      <div style={{ 
        flexShrink: 0,
        paddingTop: '6px',
        paddingBottom: '2px'
      }}>
        {/* Top Navigation Bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 12px',
          marginBottom: '8px'
        }}>
          {/* Home Button */}
          <motion.button
            onClick={() => navigate('/dashboard')}
            whileTap={{ scale: 0.95 }}
            style={{
              background: 'rgba(237, 193, 97, 0.15)',
              border: '1px solid rgba(237, 193, 97, 0.3)',
              borderRadius: '8px',
              padding: '6px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <Home size={14} style={{ color: '#edc161' }} />
            <span style={{ color: '#edc161', fontSize: 'clamp(10px, 2.5vw, 11px)', fontWeight: 600 }}>Dashboard</span>
          </motion.button>

          {/* Spacer — domain indicator removed (no swipe, user switches via pills below) */}
          <div style={{ width: '60px' }} />
        </div>

        {/* Language Pills */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '4px',
          marginBottom: '4px'
        }}>
          <div style={{
            display: 'flex',
            gap: '4px',
            background: 'rgba(10, 22, 40, 0.95)',
            backdropFilter: 'blur(15px)',
            padding: '5px 10px',
            borderRadius: '18px',
            border: '1px solid rgba(237, 193, 97, 0.4)'
          }}>
            {LANGUAGES.map(lang => (
              <button
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                style={{
                  padding: '4px 10px',
                  borderRadius: '12px',
                  border: 'none',
                  background: selectedLanguage === lang ? 'rgba(237, 193, 97, 0.35)' : 'transparent',
                  color: selectedLanguage === lang ? '#edc161' : '#9CA3AF',
                  fontSize: 'clamp(10px, 2.5vw, 11px)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {LANGUAGE_LABELS[lang]}
              </button>
            ))}
          </div>
        </div>

        {/* Title - Golden branded header matching dashboard */}
        <div style={{ textAlign: 'center' }}>
          <h1 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(18px, 5vw, 24px)',
            fontWeight: 400,
            color: '#F3E5AB',
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            margin: 0,
            textShadow: '0 0 20px rgba(237, 193, 97, 0.45)',
            lineHeight: 1.1,
          }}>
            {personName}&apos;s {currentDomain.name} Future
          </h1>
          <p style={{ 
            fontSize: 'clamp(9px, 2.5vw, 11px)', 
            color: 'rgba(243,229,171,0.55)',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            margin: '4px 0 0',
            fontWeight: 500,
          }}>
            {futureMahadashas.length > 0 
              ? `${futureMahadashas.length} Future Mahadashas • ~10 min story`
              : 'Your Future Predictions • ~10 min story'
            }
          </p>
        </div>
      </div>

      {/* ===== DOMAIN SWIPE NAVIGATION ===== */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 8px',
        flexShrink: 0,
        height: 'clamp(100px, 18vh, 140px)'
      }}>
        {/* Spacer (swipe removed) */}
        <div style={{ width: '32px', flexShrink: 0 }} />

        {/* Sage + Play Controls */}
        <div style={{ 
          textAlign: 'center', 
          padding: '0 4px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {/* Sage Avatar */}
          <motion.img
            src="/sage-final.png"
            alt="Sage"
            animate={{
              y: isPlaying ? [0, -5, 0] : [0, -2, 0],
              filter: isPlaying
                ? `drop-shadow(0 0 25px ${currentMahadasha.color || '#edc161'})`
                : 'drop-shadow(0 0 12px rgba(237, 193, 97, 0.4))'
            }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            style={{ 
              width: 'clamp(60px, 15vw, 80px)', 
              height: 'auto'
            }}
          />

          {/* Play/Pause Button */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}
          >
            {storyComplete ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}
              >
                <button
                  onClick={handleReplay}
                  style={{
                    padding: '8px 16px',
                    fontSize: 'clamp(10px, 2.8vw, 12px)',
                    fontWeight: 600,
                    background: 'transparent',
                    border: '2px solid #edc161',
                    borderRadius: '20px',
                    color: '#edc161',
                    cursor: 'pointer'
                  }}
                >
                  Replay
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  style={{
                    padding: '8px 18px',
                    fontSize: 'clamp(10px, 2.8vw, 12px)',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #edc161, #d4a84b)',
                    border: 'none',
                    borderRadius: '20px',
                    color: '#020712',
                    cursor: 'pointer'
                  }}
                >
                  Dashboard
                </button>
              </motion.div>
            ) : (
              <>
                <motion.button
                  onClick={handlePlayPauseToggle}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #edc161, #d4a84b)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 25px rgba(237, 193, 97, 0.6)'
                  }}
                >
                  {isPlaying ? (
                    <Pause style={{ width: '20px', height: '20px', color: '#020712' }} />
                  ) : (
                    <Play style={{ width: '20px', height: '20px', color: '#020712', marginLeft: '2px' }} />
                  )}
                </motion.button>
                <p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: '#edc161', fontWeight: 600, margin: 0 }}>
                  {isPlaying ? 'Tap to Pause' : storyComplete ? 'Replay' : 'Play'}
                </p>
              </>
            )}
          </motion.div>
        </div>

        {/* Spacer (swipe removed) */}
        <div style={{ width: '32px', flexShrink: 0 }} />
      </div>

      {/* ===== TEXT SECTION: Narration Box ===== */}
      <div style={{ 
        flex: 1,
        minHeight: 0,
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'flex-start',
        padding: '0 clamp(20px, 5vw, 32px)',
        overflow: 'hidden',
        transition: 'all 0.3s ease'
      }}>
        {displayedText && (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
            {/* Speech bubble pointer */}
            <div style={{
              width: 0,
              height: 0,
              margin: '0 auto',
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              borderBottom: `10px solid ${currentMahadasha.color ? currentMahadasha.color + '40' : 'rgba(237, 193, 97, 0.3)'}`
            }} />
            
            {/* Text box - content-sized, scrollable when overflow */}
            <div 
              id="future-text-box"
              style={{
              padding: 'clamp(12px, 3vw, 16px) clamp(14px, 3.5vw, 20px)',
              background: `linear-gradient(135deg, ${currentMahadasha.color ? currentMahadasha.color + '18' : 'rgba(237, 193, 97, 0.12)'}, rgba(2, 7, 18, 0.95))`,
              backdropFilter: 'blur(20px)',
              borderRadius: 'clamp(10px, 2.5vw, 14px)',
              border: `1.5px solid ${currentMahadasha.color ? currentMahadasha.color + '50' : 'rgba(237, 193, 97, 0.35)'}`,
              maxHeight: '100%',
              overflowY: 'auto',
              touchAction: 'pan-y',
              transition: 'border-color 0.5s ease, background 0.5s ease',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
              <p style={{
                fontSize: 'clamp(16px, 4.5vw, 20px)',
                lineHeight: '1.85',
                color: '#F3E5AB',
                margin: 0,
                textAlign: 'center',
                whiteSpace: 'pre-line',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                padding: '0 4px',
                fontWeight: 400,
              }}>
                {storyComplete ? fullStoryTextRef.current[selectedLanguage] || displayedText : displayedText}
              </p>
            </div>

            {/* 12-month drip reminder moved to bottom bar only (no duplicate here) */}

            {/* Current Mahadasha Indicator removed — already shown as active in timeline below */}
          </div>
        )}
      </div>

      {/* ===== BOTTOM SECTION: Timeline (~20%) - EXACT COPY OF PAST STORY ===== */}
      <div style={{ 
        flexShrink: 0,
        padding: '8px 6px 16px',
      }}>
        {futureMahadashas.length > 0 && (
        <div style={{ position: 'relative', height: 'clamp(85px, 15vh, 115px)', width: '100%' }}>
          {/* Base line - positioned to align with dot centers */}
          <div style={{
            position: 'absolute',
            top: '18px',
            left: '5%',
            right: '5%',
            height: '2px',
            background: 'rgba(237, 193, 97, 0.25)',
          }} />

          {/* Progress line */}
          <motion.div
            animate={{
              scaleX: currentMahadashaIndex >= 0 ? (currentMahadashaIndex + 1) / futureMahadashas.length : 0,
              opacity: isPlaying || displayedText ? 1 : 0
            }}
            transition={{ duration: 0.8 }}
            style={{
              position: 'absolute',
              top: '18px',
              left: '5%',
              right: '5%',
              height: '3px',
              background: 'linear-gradient(90deg, #edc161, #edc161)',
              transformOrigin: 'left',
              borderRadius: '2px',
              boxShadow: '0 0 12px #edc161'
            }}
          />

          {/* Timeline dots - Responsive */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '0 5%',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            alignItems: 'flex-start',
            paddingTop: '8px'
          }}>
            {futureMahadashas.map((period, idx) => {
              const planetColor = period.color || PLANET_COLORS[period.lord] || '#edc161';
              
              // 12-month drip: A mahadasha is LOCKED only if none of its chunks fall in the 1/12 slice.
              const isLocked = !unlockedMdIndices.includes(idx);
              // Active highlight follows the currently playing chunk's mahadasha
              // (set per-chunk by playback engine; overview maps to first future MD).
              const isActive = idx === currentMahadashaIndex && !isLocked;
              const isPast = idx < currentMahadashaIndex && !isLocked;
              
              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: isActive ? 50 : 10,
                    minWidth: isActive ? '50px' : '30px'
                  }}
                >
                  {/* Glow animations - show for active mahadasha */}
                  {isActive && (
                    <>
                      <motion.div
                        animate={{ scale: [1, 1.6, 1], opacity: [0.4, 0, 0.4] }}
                        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                        style={{
                          position: 'absolute',
                          width: 'clamp(50px, 12vw, 70px)',
                          height: 'clamp(50px, 12vw, 70px)',
                          borderRadius: '50%',
                          border: `2px solid ${planetColor}`,
                          pointerEvents: 'none'
                        }}
                      />
                      <motion.div
                        animate={{ scale: [1, 1.3, 1], opacity: [0.6, 0, 0.6] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                        style={{
                          position: 'absolute',
                          width: 'clamp(40px, 10vw, 55px)',
                          height: 'clamp(40px, 10vw, 55px)',
                          borderRadius: '50%',
                          border: `2px solid ${planetColor}`,
                          pointerEvents: 'none'
                        }}
                      />
                    </>
                  )}
                  
                  {/* Main dot with icon - Responsive */}
                  <motion.div
                    animate={{
                      width: isActive ? 'clamp(35px, 10vw, 45px)' : isPast ? 'clamp(14px, 4vw, 18px)' : 'clamp(18px, 4.5vw, 22px)',
                      height: isActive ? 'clamp(35px, 10vw, 45px)' : isPast ? 'clamp(14px, 4vw, 18px)' : 'clamp(18px, 4.5vw, 22px)',
                      borderColor: isLocked ? 'rgba(237, 193, 97, 0.35)' : isActive ? planetColor : isPast ? '#edc161' : 'rgba(237, 193, 97, 0.4)'
                    }}
                    transition={{ duration: 0.5 }}
                    style={{
                      borderRadius: '50%',
                      background: isLocked
                        ? 'rgba(237, 193, 97, 0.08)'
                        : isActive
                          ? `radial-gradient(circle, ${planetColor}60, ${planetColor}20)`
                          : isPast 
                            ? '#edc161'
                            : 'rgba(237, 193, 97, 0.2)',
                      border: `${isLocked ? 1.5 : 2}px solid ${isLocked ? 'rgba(237, 193, 97, 0.35)' : isActive ? planetColor : isPast ? '#edc161' : 'rgba(237, 193, 97, 0.4)'}`,
                      boxShadow: isActive && !isLocked
                        ? `0 0 20px ${planetColor}, 0 0 40px ${planetColor}50`
                        : isPast && !isLocked
                          ? '0 0 6px rgba(237, 193, 97, 0.3)'
                          : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: isActive ? 'clamp(16px, 5vw, 20px)' : isPast ? 'clamp(8px, 2vw, 10px)' : '0px',
                      opacity: 1,
                    }}
                  >
                    {isLocked ? (
                      <Lock size={10} strokeWidth={2.2} style={{ color: 'rgba(237,193,97,0.7)' }} />
                    ) : (isActive || isPast) && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {period.icon || PLANET_ICONS[period.lord]}
                      </motion.span>
                    )}
                  </motion.div>
                  
                  {/* Year label */}
                  <span style={{
                    fontSize: 'clamp(8px, 2.2vw, 10px)',
                    color: isLocked ? 'rgba(237, 193, 97, 0.5)' : isActive ? planetColor : isPast ? '#edc161' : 'rgba(237, 193, 97, 0.5)',
                    marginTop: '4px',
                    fontWeight: 600,
                    opacity: isLocked ? 0.7 : isActive ? 1 : isPast ? 0.8 : 0.5,
                    whiteSpace: 'nowrap'
                  }}>
                    {period.years}
                  </span>
                  
                  {/* Planet name - show for all dots */}
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isLocked ? 0.55 : isActive ? 1 : 0.6 }}
                    style={{
                      fontSize: isActive ? 'clamp(9px, 2.5vw, 11px)' : 'clamp(7px, 2vw, 9px)',
                      color: isLocked ? 'rgba(237, 193, 97, 0.5)' : isActive ? planetColor : isPast ? '#edc161' : 'rgba(237, 193, 97, 0.5)',
                      fontWeight: isActive ? 700 : 500,
                      marginTop: '1px'
                    }}
                  >
                    {period.lord}
                  </motion.span>
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* Domain Pills - Quick Navigation (only ready domains clickable) */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 'clamp(4px, 1vw, 6px)',
          marginTop: '8px',
          flexWrap: 'wrap',
          padding: '0 8px'
        }}>
          {ALL_DOMAINS.map((domain) => {
            const isCurrent = domain.key === resolvedDomainKey;
            const isReady = !!audioReadyDomainsRef.current[domain.key];
            const canNavigate = isCurrent || isReady;
            return (
              <motion.button
                key={domain.key}
                onClick={() => {
                  if (!canNavigate) {
                    return; // silently block — domain not ready
                  }
                  if (domain.key !== resolvedDomainKey) {
                    stopAllAudio();
                    setIsPlaying(false);
                    setDisplayedText('');
                    previousTextRef.current = '';
                    displayedTextRef.current = '';
                    navigate(`/future-story/${domain.key}`);
                  }
                }}
                whileTap={canNavigate ? { scale: 0.95 } : {}}
                style={{
                  padding: 'clamp(4px, 1vw, 5px) clamp(8px, 2vw, 12px)',
                  borderRadius: '12px',
                  border: isCurrent 
                    ? '1px solid #edc161' 
                    : canNavigate
                      ? '1px solid rgba(237, 193, 97, 0.3)'
                      : '1px solid rgba(237, 193, 97, 0.1)',
                  background: isCurrent 
                    ? 'rgba(237, 193, 97, 0.25)' 
                    : canNavigate
                      ? 'rgba(237, 193, 97, 0.08)'
                      : 'rgba(237, 193, 97, 0.02)',
                  color: isCurrent ? '#edc161' : canNavigate ? 'rgba(237, 193, 97, 0.6)' : 'rgba(237, 193, 97, 0.2)',
                  fontSize: 'clamp(9px, 2.2vw, 11px)',
                  fontWeight: 600,
                  cursor: canNavigate ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                  opacity: canNavigate ? 1 : 0.4,
                }}
              >
                {domain.name}
              </motion.button>
            );
          })}
        </div>
        
        {/* Domain pills end */}
        
        {/* Monthly Drip: Next unlock timer */}
        {unlockedMonths < 12 && (
          <div data-testid="future-drip-lock" style={{
            margin: '12px 16px 0', padding: '10px 14px', borderRadius: '10px',
            background: 'rgba(237,193,97,0.04)', border: '1px solid rgba(237,193,97,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(237,193,97,0.08)', border: '1px solid rgba(237,193,97,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Lock size={13} strokeWidth={2.2} style={{ color: 'rgba(237,193,97,0.9)' }} />
              </div>
              <div>
                <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.8)', fontWeight: '500', margin: 0 }}>
                  Part {unlockedMonths}/12 unlocked
                </p>
                <p style={{ fontSize: 'clamp(8px, 2.2vw, 9px)', color: 'rgba(237,193,97,0.6)', fontWeight: '400', margin: '1px 0 0 0' }}>
                  Next part in {daysUntilUnlock} days
                </p>
              </div>
            </div>
            <div style={{ padding: '4px 10px', borderRadius: '8px', background: 'rgba(237,193,97,0.06)', border: '1px solid rgba(237,193,97,0.1)' }}>
              <span style={{ fontSize: 'clamp(9px, 2.4vw, 10px)', color: 'rgba(237,193,97,0.7)', fontWeight: '600', fontFamily: 'monospace' }}>
                {daysUntilUnlock}d left
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FutureStoryExact;
